import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, useRef, useEffect, useMemo } from "react";
import { normalizeRole } from "@/lib/rbac";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  OPERATIONAL_QUERY_OPTIONS,
  ADMIN_QUERY_KEYS,
  invalidateAllAdminState,
  invalidateAllTLState,
  invalidateAllStudentState,
} from "@/lib/api/query-options";
import {
  fetchStudentGrid,
  fetchStudentStatusCounts,
  fetchLearningTracks,
  updateProfile,
  updateStudentProfile,
  adminManageUser,
  resetStudentProgress,
  forceCompleteTraining,
  fetchStudentDashboardCourses,
  fetchTeams,
  removeTeamMembers,
  fetchActiveTeamLeaders,
  resetStudentPassword,
} from "@/lib/api";
import { fetchStudentExamSessions, fetchExams } from "@/lib/api/exams";
import { fetchBatchesAll } from "@/lib/api/teams";
import { createStudent, STUDY_LEVELS, studyLevelLabel } from "@/lib/api/students";
import { API_BASE_URL, getAuthHeaders } from "@/lib/api-client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  PieChart,
  Pie,
  Tooltip as RechartsTooltip,
  LabelList,
} from "recharts";
import { toast } from "sonner";
import {
  Users,
  Search,
  CheckCircle2,
  XCircle,
  GraduationCap,
  FilterX,
  RefreshCw,
  Upload,
  Download,
  FileText,
  Trash2,
  RotateCcw,
  UserPlus,
  Edit,
  Camera,
  ChevronDown,
  ChevronUp,
  SlidersHorizontal,
  MoreHorizontal,
  X,
  Plus,
  Loader2,
  BookOpen,
  BarChart3,
  Activity,
  TrendingUp,
  Wifi,
  MessageCircle,
  KeyRound,
  Copy,
  ImagePlus,
  Maximize2,
  Eye,
  LayoutGrid,
  Rows3,
} from "lucide-react";
import type { Profile } from "@/lib/types";
import { useAuth } from "@/hooks/use-auth";
import { StudentActivityLog } from "@/components/student-activity-log";
import { PaginationControls } from "@/components/PaginationControls";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { AccessDenied } from "@/components/auth/AccessDenied";

function getTeamLeadDisplay(
  teamLeadName: string | null | undefined,
  allProfiles: any[],
  allRoles: any[],
): string {
  if (!teamLeadName) return "—";

  const leaderProfile = allProfiles.find((p) => {
    const isStaff = allRoles.some(
      (r) =>
        r.user_id === p.id &&
        (normalizeRole(r.role) === "SystemAdmin" || normalizeRole(r.role) === "TeamLeader"),
    );
    return isStaff && p.full_name?.trim().toLowerCase() === teamLeadName.trim().toLowerCase();
  });

  if (leaderProfile && leaderProfile.student_id) {
    return `${teamLeadName} (${leaderProfile.student_id})`;
  }

  return teamLeadName;
}

export type AdminStudentsSearch = {
  status?: string;
};

// Admin-only: the org-wide roster lives here. TeamLeaders use their own scoped
// /team-leader/students page (same backend endpoint, auto-scoped server-side to their groups) —
// this page is not their route to the same data.
export const Route = createFileRoute("/_authenticated/admin/students")({
  validateSearch: (search: Record<string, unknown>): AdminStudentsSearch => ({
    status: typeof search.status === "string" ? search.status : undefined,
  }),
  component: () => (
    <RequirePermission
      action="manage:users"
      fallback={<AccessDenied message="You do not have permission to view the student roster." />}
    >
      <StudentsAdminPage />
    </RequirePermission>
  ),
});

type UserRole = { user_id: string; role: string };

const TRAINING_STATUSES = [
  "In Training",
  "Passed Training",
  "Failed check",
  "Withdraw",
  "Withdraw after training",
  "Not recommended",
  "Not responding",
  "Black list",
] as const;

type TrainingStatus = (typeof TRAINING_STATUSES)[number];

// The program-lifecycle subset (no flags). Used by the student status EDITOR, which now sets
// the lifecycle and the blacklist / not-responding flags as independent, orthogonal controls.
// (TRAINING_STATUSES keeps "Black list"/"Not responding" for the filter chips.)
const LIFECYCLE_STATUSES = [
  "In Training",
  "Passed Training",
  "Failed check",
  "Withdraw",
  "Withdraw after training",
  "Not recommended",
] as const;

const STATUS_BADGE: Record<TrainingStatus, string> = {
  "In Training": "bg-primary/10 text-primary border-primary/20",
  "Passed Training": "bg-success/15 text-success border-success/30",
  "Failed check": "bg-destructive-muted text-destructive border-destructive/25",
  Withdraw: "bg-warning-muted text-warning border-warning/30",
  "Withdraw after training": "bg-warning/15 text-warning border-warning/25",
  "Not recommended": "bg-destructive/10 text-destructive border-destructive/20",
  "Not responding": "bg-muted text-muted-foreground border-border",
  "Black list": "bg-destructive/20 text-destructive border-destructive/40",
};

const STATUS_DOT_COLORS: Record<TrainingStatus, string> = {
  "In Training": "bg-primary",
  "Passed Training": "bg-success",
  "Failed check": "bg-destructive",
  Withdraw: "bg-warning",
  "Withdraw after training": "bg-warning",
  "Not recommended": "bg-destructive",
  "Not responding": "bg-muted-foreground",
  "Black list": "bg-destructive",
};

// The three live-activity facets, mirroring the online / offline / neverActive counts the status
// endpoint returns. Held as data so the filter rail can render them in one loop instead of three
// near-identical hand-written buttons.
const ACTIVITY_FILTERS = [
  {
    key: "online",
    label: "Online Now",
    dotClass: "bg-success",
    selectedClass: "bg-success-muted text-success border-success/25",
    ringClass: "ring-success",
    badgeClass: "bg-success/20 text-success",
  },
  {
    key: "offline",
    label: "Offline",
    dotClass: "bg-muted-foreground",
    selectedClass: "bg-muted text-foreground border-border",
    ringClass: "ring-muted-foreground/40",
    badgeClass: "bg-muted-foreground/20 text-foreground",
  },
  {
    key: "never",
    label: "Never Active",
    dotClass: "bg-warning",
    selectedClass: "bg-warning-muted text-warning border-warning/30",
    ringClass: "ring-warning",
    badgeClass: "bg-warning/20 text-warning",
  },
] as const;

// Chart fill per training status — references the existing brand CSS tokens
// (hex values in styles.css) so charts stay on-palette in both light and dark.
const STATUS_CHART_COLOR: Record<TrainingStatus, string> = {
  "In Training": "var(--primary)",
  "Passed Training": "var(--success)",
  "Failed check": "var(--destructive)",
  Withdraw: "var(--warning)",
  "Withdraw after training": "var(--warning)",
  "Not recommended": "var(--destructive)",
  "Not responding": "var(--muted-foreground)",
  "Black list": "var(--destructive)",
};

function getDisplayTrainingStatus(s: string | null | undefined): string {
  if (!s) return "";
  if (s === "Failed Training") return "Failed check";
  return s;
}

function statusBadgeClass(s: string | null | undefined): string {
  const status = getDisplayTrainingStatus(s);
  return STATUS_BADGE[status as TrainingStatus] ?? "bg-muted text-muted-foreground border-border";
}

// Maps between the backend TrainingStatus enum names (LMSAPI.DAL.Enums.TrainingStatus)
// and the human-readable labels shown in the UI. "Not responding" and "Black list"
// are not part of the enum — they are derived from the IsNotResponding/IsBlacklisted flags.
const BACKEND_TO_UI_STATUS: Record<string, TrainingStatus> = {
  InTraining: "In Training",
  Passed: "Passed Training",
  Failed: "Failed check",
  WithdrawnDuringTraining: "Withdraw",
  WithdrawnAfterTraining: "Withdraw after training",
  NotRecommended: "Not recommended",
};

const UI_TO_BACKEND_STATUS: Record<string, string> = Object.fromEntries(
  Object.entries(BACKEND_TO_UI_STATUS).map(([backend, ui]) => [ui, backend]),
);

function mapBackendStatusToUI(s: string | null | undefined): TrainingStatus | "" {
  if (!s) return "";
  return BACKEND_TO_UI_STATUS[s] ?? (s as TrainingStatus);
}

function mapUIToBackendStatus(s: string | null | undefined): string {
  if (!s) return "";
  return UI_TO_BACKEND_STATUS[s] ?? s;
}

// Orthogonal student-status editor: a lifecycle dropdown PLUS independent blacklist /
// not-responding toggles. Each control emits only its own field, so setting the lifecycle
// never clears a flag and vice versa (mirrors the backend's orthogonal model).
type StatusPatch = { trainingStatus?: string; isBlacklisted?: boolean; isNotResponding?: boolean };

function StudentStatusEditor({
  lifecycle,
  isBlacklisted,
  isNotResponding,
  badgeClass,
  triggerClass,
  onChange,
}: {
  lifecycle: string;
  isBlacklisted: boolean;
  isNotResponding: boolean;
  badgeClass: string;
  triggerClass?: string;
  onChange: (patch: StatusPatch) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5" onClick={(e) => e.stopPropagation()}>
      <Select value={lifecycle} onValueChange={(val) => onChange({ trainingStatus: val })}>
        <SelectTrigger
          className={[triggerClass ?? "h-7 text-xs border px-2 w-40", badgeClass].join(" ")}
        >
          <SelectValue placeholder="Set status" />
        </SelectTrigger>
        <SelectContent>
          {LIFECYCLE_STATUSES.map((s) => (
            <SelectItem key={s} value={s} className="text-xs">
              {s}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Derive up-to-two-letter initials from a name or email for the avatar fallback.
function getInitials(name: string | null | undefined, email?: string | null): string {
  const source = (name && name.trim()) || (email ? email.split("@")[0] : "");
  if (!source) return "?";
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return source.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Deterministically pick a soft tint for the avatar fallback based on the id/name,
// so avatars are visually distinct without introducing new brand colours.
const AVATAR_TINTS = [
  "bg-primary/12 text-primary",
  "bg-success/12 text-success",
  "bg-warning/15 text-warning",
  "bg-destructive/12 text-destructive",
  "bg-muted-foreground/15 text-foreground",
] as const;

function avatarTint(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_TINTS[hash % AVATAR_TINTS.length];
}

function StudentAvatar({
  name,
  email,
  avatarUrl,
  seed,
  className,
}: {
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  seed: string;
  className?: string;
}) {
  return (
    <Avatar className={className}>
      {avatarUrl ? <AvatarImage src={avatarUrl} alt={name ?? email ?? "Student"} /> : null}
      <AvatarFallback className={`font-semibold ${avatarTint(seed)}`}>
        {getInitials(name, email)}
      </AvatarFallback>
    </Avatar>
  );
}

const STUDENT_PAGE_SIZE = 50;
const STUDENT_VIEW_MODE_KEY = "admin_students_view_mode";

// Every facet below is applied by the SERVER (StudentFilterQuery in
// LMSAPI.BLL/Dtos/Student/StudentFilterQuery.cs). `backend` is the enum member name that DTO
// binds; the UI keeps its own lowercase `value` so the existing markup and URLs are unchanged.
// These used to be filtered in the browser, which silently narrowed only the current page of
// results rather than the roster.
const EXAM_FILTER_OPTIONS = [
  { value: "nottaken", backend: "NotTaken", label: "Not taken" },
  { value: "inprogress", backend: "InProgress", label: "In progress" },
  { value: "passed", backend: "Passed", label: "Passed" },
  { value: "failed", backend: "Failed", label: "Failed" },
  { value: "abandoned", backend: "Abandoned", label: "Abandoned" },
];

const PROGRESS_FILTER_OPTIONS: { value: string; backend: string; label: string }[] = [
  { value: "notstarted", backend: "NotStarted", label: "Not started (0%)" },
  { value: "started", backend: "Started", label: "Started (1–99%)" },
  { value: "q1", backend: "Q1", label: "1–25%" },
  { value: "q2", backend: "Q2", label: "26–50%" },
  { value: "q3", backend: "Q3", label: "51–75%" },
  { value: "q4", backend: "Q4", label: "76–99%" },
  { value: "nearlydone", backend: "NearlyDone", label: "Nearly done (90–99%)" },
  { value: "completed", backend: "Completed", label: "Completed (100%)" },
];

/**
 * Reads a percent typed into the progress range inputs. Blank or non-numeric means "unbounded"
 * rather than 0, so a half-typed "-" or a cleared field does not silently exclude every row.
 */
function parseProgressBound(raw: string): number | null {
  if (raw.trim() === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, n));
}

// Server-side sort. Mirrors the switch in StudentService.GetStudentGridAsync; anything else there
// falls through to CreatedAt, which is what "Date added" selects.
const SORT_OPTIONS = [
  { value: "createdat", label: "Date added" },
  { value: "name", label: "Name" },
  { value: "lastseen", label: "Last seen" },
  { value: "trainingstatus", label: "Training status" },
];

/** Holds `value` back until it has stopped changing for `delayMs`. */
function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}
// Shape produced by mapping a StudentGridRow into the UI. Extends Profile so it
// can be passed anywhere a Profile is expected, plus the extra grid-only metrics.
type StudentRow = Profile & {
  training_status: string; // combined label (flag takes precedence) — for badges/filters
  training_lifecycle: string; // pure lifecycle label — for the editor dropdown
  is_blacklisted: boolean;
  is_not_responding: boolean;
  status: string;
  last_seen_at: string | null;
  _completedModules: number;
  _totalModules: number;
  _progressPercent: number;
  _examStatus: string;
  _examScore: number | null;
  _examPassed: boolean | null;
};

// ─── Analytics ────────────────────────────────────────────────────────────────
function StatTile({
  label,
  value,
  icon: Icon,
  tone,
  className,
}: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  tone: string;
  className?: string;
}) {
  return (
    <div
      className={`flex items-center gap-3 rounded-xl border bg-card p-3 shadow-sm ${className ?? ""}`}
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-bold leading-none text-foreground">{value}</p>
        <p className="mt-1 truncate text-[11px] font-medium text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function StudentAnalytics({
  counts,
  total,
  online,
  offline,
  neverActive,
  avgProgress,
  loadedCount,
  insightsOpen,
  onToggleInsights,
}: {
  counts: Record<string, number | undefined>;
  total: number;
  online: number;
  offline: number;
  neverActive: number;
  avgProgress: number;
  loadedCount: number;
  insightsOpen: boolean;
  onToggleInsights: () => void;
}) {
  const statusData = TRAINING_STATUSES.map((s) => ({
    name: s,
    value: counts[s] ?? 0,
    fill: STATUS_CHART_COLOR[s],
  })).filter((d) => d.value > 0);

  const activityData = [
    { name: "Online", value: online, fill: "var(--success)" },
    { name: "Offline", value: offline, fill: "var(--muted-foreground)" },
    { name: "Never active", value: neverActive, fill: "var(--warning)" },
  ].filter((d) => d.value > 0);

  // "Active Student" was folded into "In Training"; the active-students tile now tracks that.
  const activeStudents = counts["In Training"] ?? 0;

  const tooltipStyle = {
    background: "var(--popover)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    fontSize: 12,
    color: "var(--popover-foreground)",
  } as const;

  return (
    <div className="space-y-4">
      {/* KPI strip — the roster is the page; the numbers ride above it in one row. */}
      <div className="flex flex-wrap items-stretch gap-3">
        <StatTile
          label="Total students"
          value={total}
          icon={Users}
          tone="bg-primary/10 text-primary"
          className="min-w-[170px] flex-1"
        />
        <StatTile
          label="Active students"
          value={activeStudents}
          icon={GraduationCap}
          tone="bg-success/10 text-success"
          className="min-w-[170px] flex-1"
        />
        <StatTile
          label="Online now"
          value={online}
          icon={Wifi}
          tone="bg-success/10 text-success"
          className="min-w-[170px] flex-1"
        />
        <StatTile
          label={`Avg. progress (${loadedCount} loaded)`}
          value={`${avgProgress}%`}
          icon={TrendingUp}
          tone="bg-warning/15 text-warning"
          className="min-w-[170px] flex-1"
        />
        <button
          type="button"
          onClick={onToggleInsights}
          aria-expanded={insightsOpen}
          className={[
            "flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-xl border px-4 text-[13px] font-semibold shadow-sm transition-colors",
            insightsOpen
              ? "border-primary/20 bg-primary/10 text-primary"
              : "bg-card text-foreground hover:bg-muted/50",
          ].join(" ")}
        >
          <BarChart3 className="h-4 w-4 text-primary" />
          Insights
          {insightsOpen ? (
            <ChevronUp className="h-4 w-4 opacity-70" />
          ) : (
            <ChevronDown className="h-4 w-4 opacity-70" />
          )}
        </button>
      </div>

      {/* Charts — collapsed by default so the roster stays above the fold. */}
      {insightsOpen && (
        <div className="space-y-3 rounded-2xl border bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Overview & Analytics
              </h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleInsights}
              className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground"
            >
              Hide
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Training status distribution */}
            <div className="lg:col-span-2 rounded-xl border bg-background/40 p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Training status distribution
              </p>
              {statusData.length === 0 ? (
                <div className="flex h-[220px] items-center justify-center text-xs text-muted-foreground">
                  No status data yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={statusData}
                    layout="vertical"
                    margin={{ top: 4, right: 24, bottom: 4, left: 8 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={124}
                      tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <RechartsTooltip
                      cursor={{ fill: "var(--muted)", opacity: 0.4 }}
                      contentStyle={tooltipStyle}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={20}>
                      {statusData.map((d) => (
                        <Cell key={d.name} fill={d.fill} />
                      ))}
                      <LabelList
                        dataKey="value"
                        position="right"
                        style={{ fontSize: 11, fill: "var(--foreground)" }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Activity split */}
            <div className="rounded-xl border bg-background/40 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Activity className="h-3.5 w-3.5" />
                Live activity
              </p>
              {activityData.length === 0 ? (
                <div className="flex h-[220px] items-center justify-center text-xs text-muted-foreground">
                  No activity data yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={activityData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={48}
                      outerRadius={78}
                      paddingAngle={2}
                      strokeWidth={2}
                      stroke="var(--card)"
                    >
                      {activityData.map((d) => (
                        <Cell key={d.name} fill={d.fill} />
                      ))}
                    </Pie>
                    <RechartsTooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              )}
              <div className="mt-1 flex flex-wrap justify-center gap-x-4 gap-y-1">
                {activityData.map((d) => (
                  <span
                    key={d.name}
                    className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
                  >
                    <span className="h-2 w-2 rounded-full" style={{ background: d.fill }} />
                    {d.name} ({d.value})
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StudentsAdminPage() {
  const { user, hasPermission, isSuperAdmin, impersonate } = useAuth();
  const navigate = useNavigate();
  // Destructive roster actions (delete, bulk-delete, reset, force-complete) require
  // manage:users, which only SystemAdmin holds. TeamLeaders get a
  // read-only roster — matching the SystemAdmin-only backend on those mutations.
  const isLeaderOnly = !hasPermission("manage:users");
  // Card-only layout — the desktop table view has been removed per admin request.

  const rawSearch = useSearch({ strict: false });
  const searchParams = (rawSearch ?? {}) as AdminStudentsSearch;

  const [search, setSearch] = useState("");
  // The roster query is server-side, so an undebounced box fired one request per keystroke.
  const debouncedSearch = useDebouncedValue(search, 300);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(() => {
    if (searchParams.status === "Not responding" || searchParams.status === "notResponding") {
      return new Set(["Not responding"]);
    }
    if (searchParams.status === "Black list" || searchParams.status === "blacklisted") {
      return new Set(["Black list"]);
    }
    if (searchParams.status) {
      return new Set([searchParams.status]);
    }
    return new Set();
  });
  const [projectFilter, setProjectFilter] = useState("all");
  const [onlineFilter, setOnlineFilter] = useState("all");
  const [teamLeadFilter, setTeamLeadFilter] = useState("all");
  const [studyFilter, setStudyFilter] = useState("all");
  const [batchFilter, setBatchFilter] = useState("all");
  const [examFilter, setExamFilter] = useState("all");
  const [progressFilter, setProgressFilter] = useState("all");
  // Free-form percent bounds, ANDed with the bucket above for ranges no preset covers.
  const [progressMin, setProgressMin] = useState("");
  const [progressMax, setProgressMax] = useState("");
  // Debounced for the same reason as the search box: these are server facets, and typing "40"
  // otherwise requests the roster at 4% and again at 40%.
  const debouncedProgressMin = useDebouncedValue(progressMin, 300);
  const debouncedProgressMax = useDebouncedValue(progressMax, 300);
  const [groupAssignmentFilter, setGroupAssignmentFilter] = useState("all");
  const [attentionFilter, setAttentionFilter] = useState("all");

  const [sortBy, setSortBy] = useState("createdat");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // Charts are collapsed by default: the roster is what this page is for, and the analytics
  // block used to push the first student row about 900px down the page.
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  // Roster layout preference. Persisted per browser so an admin who prefers the dense list
  // does not have to re-pick it on every visit.
  const [viewMode, setViewMode] = useState<"card" | "list">(() => {
    if (typeof window === "undefined") return "card";
    return window.localStorage.getItem(STUDENT_VIEW_MODE_KEY) === "list" ? "list" : "card";
  });
  useEffect(() => {
    window.localStorage.setItem(STUDENT_VIEW_MODE_KEY, viewMode);
  }, [viewMode]);
  // Server-side pagination. Replaces the old "Load more" control, which kept re-requesting
  // pageNumber 1 with an ever-growing pageSize — so reaching page 10 meant transferring rows
  // 1–500 again on every click.
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(STUDENT_PAGE_SIZE);
  const qc = useQueryClient();

  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());

  // ── Impersonation (SuperAdmin only) ───────────────────────────────────
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  /**
   * Swaps this admin's session for one that acts as the given student.
   *
   * Confirmed first because it is genuinely disorienting: the admin loses their own navigation and
   * privileges until they exit, and a mis-click on a dense roster row would silently drop them into
   * someone else's account. On success the whole app is reloaded so no React Query cache entry
   * populated as the admin is later read as the student's.
   */
  const handleImpersonate = async (profile: Profile) => {
    const name = profile.full_name || profile.email;
    if (
      !confirm(
        `View the platform as ${name}?\n\n` +
          `You will see exactly what they see and lose your admin privileges until you exit. ` +
          `This is recorded in the audit log.`,
      )
    ) {
      return;
    }

    setImpersonatingId(profile.id);
    const error = await impersonate(profile.id);
    if (error) {
      toast.error(error);
      setImpersonatingId(null);
      return;
    }
    window.location.replace("/");
  };

  // Reset-password flow, hoisted to the page because the rows are inline JSX rather than a row
  // component: which student is awaiting confirmation, then the generated password to show once.
  const [resetPasswordFor, setResetPasswordFor] = useState<{ id: string; email: string } | null>(
    null,
  );
  const [tempPassword, setTempPassword] = useState<{ email: string; password: string } | null>(
    null,
  );
  const [avatarLightboxUrl, setAvatarLightboxUrl] = useState<string | null>(null);

  // Clear selections and return to page 1 whenever the filter set changes — page 7 of the old
  // result set is meaningless against the new one.
  useEffect(() => {
    setSelectedStudentIds(new Set());
    setPageNumber(1);
  }, [
    search,
    selectedStatuses,
    projectFilter,
    onlineFilter,
    teamLeadFilter,
    studyFilter,
    batchFilter,
    examFilter,
    progressFilter,
    progressMin,
    progressMax,
    groupAssignmentFilter,
    attentionFilter,
    sortBy,
    sortDir,
  ]);

  const toggleStatusFilter = (status: string) => {
    const next = new Set(selectedStatuses);
    if (next.has(status)) {
      next.delete(status);
    } else {
      next.add(status);
    }
    setSelectedStatuses(next);
  };

  const toggleStudentSelection = (id: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent opening the detail dialog
    }
    const next = new Set(selectedStudentIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedStudentIds(next);
  };

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: roles = [], refetch: refetchRoles } = useQuery<UserRole[]>({
    queryKey: ["admin-roles"],
    // These facet queries back SystemAdmin-only management actions (role assignment, group
    // assignment, team-lead filtering) and hit SystemAdmin-only endpoints. Read-only roles
    // (TeamLeader) reach this roster via view:roster but must NOT fire them —
    // otherwise the page floods the console with 403/404s. Gate on manage:users.
    enabled: !isLeaderOnly && typeof window !== "undefined",
    queryFn: async () => {
      const token = localStorage.getItem("accessToken");
      const res = await fetch("/api/admin/user-roles", {
        headers: {
          Authorization: `Bearer ${token ?? ""}`,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch roles");
      return res.json();
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  const { data: teams = [] } = useQuery({
    queryKey: ["admin-teams-list"],
    queryFn: fetchTeams,
    enabled: !isLeaderOnly, // /groups is SystemAdmin-only
  });

  // Batch filter options. Like the team-lead list below, these come from the dedicated endpoint
  // rather than the loaded rows, so every batch is selectable even when nobody on the current
  // page belongs to it.
  const { data: batches = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["admin-batches-list"],
    queryFn: fetchBatchesAll,
    enabled: !isLeaderOnly, // /admin/batches is SystemAdmin-only
    staleTime: 5 * 60_000,
  });

  // Team lead filter options come from the dedicated endpoint (all active team
  // leaders), not from the loaded student rows — otherwise the dropdown is empty
  // whenever the loaded page has no team_lead values.
  const { data: teamLeaders = [] } = useQuery<any[]>({
    queryKey: ["active-team-leaders"],
    queryFn: fetchActiveTeamLeaders,
    enabled: !isLeaderOnly, // /admin/active-team-leaders is SystemAdmin-only
  });

  // The selected statuses live in a Set, and React Query hashes a query key with JSON.stringify —
  // which renders EVERY Set as "{}". Selecting a chip therefore produced a key identical to the
  // previous one, so the roster was served from cache and no request was ever made. A sorted array
  // hashes by content, which is what the key needs.
  const statusKey = useMemo(() => Array.from(selectedStatuses).sort(), [selectedStatuses]);

  // Main grid query
  const {
    data: gridData,
    isLoading,
    isFetching,
    refetch: refetchGrid,
  } = useQuery({
    queryKey: [
      "student-grid",
      debouncedSearch,
      projectFilter,
      teamLeadFilter,
      statusKey,
      onlineFilter,
      attentionFilter,
      studyFilter,
      batchFilter,
      groupAssignmentFilter,
      examFilter,
      progressFilter,
      debouncedProgressMin,
      debouncedProgressMax,
      sortBy,
      sortDir,
      pageNumber,
      pageSize,
    ],
    queryFn: () => {
      const trainingStatusList: string[] = [];
      let isBlacklisted: boolean | undefined = undefined;
      let isNotResponding: boolean | undefined = undefined;

      selectedStatuses.forEach((status) => {
        if (status === "Black list") {
          isBlacklisted = true;
        } else if (status === "Not responding") {
          isNotResponding = true;
        } else {
          const mapped = mapUIToBackendStatus(status);
          if (mapped) trainingStatusList.push(mapped);
        }
      });

      let activity: string[] = [];
      if (onlineFilter === "online") activity = ["Online"];
      else if (onlineFilter === "offline") activity = ["Offline"];
      else if (onlineFilter === "never") activity = ["NeverActive"];

      const examState = EXAM_FILTER_OPTIONS.find((o) => o.value === examFilter)?.backend;
      const progressBucket = PROGRESS_FILTER_OPTIONS.find(
        (o) => o.value === progressFilter,
      )?.backend;
      const min = parseProgressBound(debouncedProgressMin);
      const max = parseProgressBound(debouncedProgressMax);

      return fetchStudentGrid({
        search: debouncedSearch.trim(),
        groupId: projectFilter === "all" ? undefined : projectFilter,
        teamLeaderId: teamLeadFilter === "all" ? undefined : teamLeadFilter,
        trainingStatus: trainingStatusList,
        isBlacklisted,
        isNotResponding,
        needsAttention:
          attentionFilter === "all" ? undefined : attentionFilter === "flagged" ? true : false,
        activity,
        studyLevel: studyFilter === "all" ? [] : [studyFilter],
        batchId: batchFilter === "all" ? undefined : batchFilter,
        hasGroup:
          groupAssignmentFilter === "all" ? undefined : groupAssignmentFilter === "assigned",
        examState: examState ? [examState] : [],
        progressBucket,
        progressMin: min ?? undefined,
        progressMax: max ?? undefined,
        sortBy,
        sortDir,
        pageNumber,
        pageSize,
      });
    },
  });

  // Chip counts query
  const { data: statusCounts, refetch: refetchCounts } = useQuery({
    queryKey: ["student-status-counts"],
    queryFn: fetchStudentStatusCounts,
  });

  function handleRefresh() {
    refetchRoles();
    refetchGrid();
    refetchCounts();
  }

  const getOnlineStatus = (lastSeenAt: string | null | undefined) => {
    if (!lastSeenAt) return "never";
    const diffMs = new Date().getTime() - new Date(lastSeenAt).getTime();
    const diffMins = diffMs / (1000 * 60);
    return diffMins <= 5 ? "online" : "offline";
  };

  const rolesByUserId = useMemo(() => {
    const map = new Map<string, string[]>();
    roles.forEach((role) => {
      const list = map.get(role.user_id) ?? [];
      list.push(role.role);
      map.set(role.user_id, list);
    });
    return map;
  }, [roles]);

  const studentProfiles = useMemo(() => {
    if (!gridData?.items) return [];
    return gridData.items.map((row): StudentRow => {
      const lifecycleUI = mapBackendStatusToUI(row.trainingStatus);
      let trainingStatusUI: string = lifecycleUI;
      if (row.isBlacklisted) trainingStatusUI = "Black list";
      else if (row.isNotResponding) trainingStatusUI = "Not responding";

      return {
        id: row.id,
        full_name: row.fullName,
        student_id: row.studentCode,
        national_id: row.nationalId,
        avatar_url: row.avatarUrl,
        email: row.email,
        phone: row.phoneNumber,
        study: row.studyLevel,
        project_assigned: row.groups.map((g) => g.groupName).join(", "),
        team_lead: row.teamLead,
        // Was never mapped, so the detail panel's Batch row read `undefined` and rendered "—"
        // even for a student whose group belongs to a batch.
        batch: row.batch ?? null,
        training_status: trainingStatusUI,
        training_lifecycle: lifecycleUI,
        is_blacklisted: !!row.isBlacklisted,
        is_not_responding: !!row.isNotResponding,
        status: "approved",
        last_seen_at: row.lastSeenAt,
        _completedModules: row.completedModules,
        _totalModules: row.totalModules,
        _progressPercent: row.progressPercent,
        _examStatus: row.examStatus,
        _examScore: row.examScore,
        _examPassed: row.examPassed ?? null,
      };
    });
  }, [gridData]);

  const latestExamByStudentId = useMemo(() => {
    const map = new Map<string, any>();
    // Map backend ExamSessionStatus enum names to the snake_case keys the badge
    // renderer checks. The backend sends PascalCase ("Completed", "InProgress",
    // "Abandoned", "Terminated", "PendingGrading"); the UI branches on snake_case.
    const statusMap: Record<string, string> = {
      Completed: "completed",
      InProgress: "in_progress",
      Abandoned: "abandoned",
      Terminated: "terminated",
      PendingGrading: "pending_grading",
    };
    if (gridData?.items) {
      gridData.items.forEach((row) => {
        if (row.examStatus !== "NotStarted") {
          map.set(row.id, {
            status: statusMap[row.examStatus] ?? row.examStatus.toLowerCase(),
            passed: row.examPassed === true,
            score: row.examScore,
          });
        }
      });
    }
    return map;
  }, [gridData]);

  // Study and Batch options come from the full enum / the batches endpoint, not from the loaded
  // rows: now that both are server facets, deriving them from the current page would hide every
  // option no student on that page happens to have.
  const uniqueStudies = STUDY_LEVELS.map((l) => l.name);
  // Display map for the grid's Team Lead column, keyed by the team lead's full
  // name, email, or id (what the grid stores in `team_lead`).
  const teamLeadDisplayMap = useMemo(() => {
    const map = new Map<string, string>();
    teamLeaders.forEach((tl) => {
      const displayName = (tl.full_name || tl.email || "").trim();
      if (!displayName) return;
      if (tl.full_name?.trim()) map.set(tl.full_name.trim().toLowerCase(), displayName);
      if (tl.email?.trim()) map.set(tl.email.trim().toLowerCase(), displayName);
      if (tl.id) map.set(tl.id.toLowerCase(), displayName);
    });
    return map;
  }, [teamLeaders]);

  const formatTeamLead = (raw?: string | null) => {
    if (!raw) return "—";
    return raw
      .split(",")
      .map((item) => {
        const trimmed = item.trim();
        return teamLeadDisplayMap.get(trimmed.toLowerCase()) ?? trimmed;
      })
      .join(", ");
  };

  // Every facet is applied by the server, so the page renders exactly the rows it was sent —
  // there is no second filtering pass, and `totalCount` describes the same set the rows come from.
  const visibleStudents = studentProfiles;

  const totalCount = gridData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const getStudentProgress = (p: any) => {
    return {
      denominator: p._totalModules ?? 0,
      progressPct: p._progressPercent ?? 0,
      completedCount: p._completedModules ?? 0,
    };
  };

  const invalidateStudentQueries = async () => {
    await Promise.all([
      invalidateAllAdminState(qc),
      invalidateAllTLState(qc),
      invalidateAllStudentState(qc),
    ]);
  };

  // ── Mutations ─────────────────────────────────────────────────────────────
  // Orthogonal status update: each control (lifecycle dropdown, blacklist toggle,
  // not-responding toggle) sends ONLY its own field, matching the backend's orthogonal model.
  const updateStudentStatus = useMutation({
    mutationFn: async ({
      id,
      trainingStatus,
      isBlacklisted,
      isNotResponding,
    }: {
      id: string;
      trainingStatus?: string;
      isBlacklisted?: boolean;
      isNotResponding?: boolean;
    }) => {
      const body: Record<string, unknown> = {};
      if (trainingStatus !== undefined) body.trainingStatus = mapUIToBackendStatus(trainingStatus);
      if (isBlacklisted !== undefined) body.isBlacklisted = isBlacklisted;
      if (isNotResponding !== undefined) body.isNotResponding = isNotResponding;
      await updateStudentProfile(id, body);
    },
    onSuccess: () => {
      invalidateStudentQueries();
      toast.success("Student status updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteStudent = useMutation({
    mutationFn: async (userId: string) => {
      await adminManageUser("delete_user", { userId });
    },
    onSuccess: () => {
      invalidateStudentQueries();
      toast.success("Student account deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Same contract as the staff grid's reset: the server returns the new password once, so it goes
  // straight into the dialog state rather than a toast that could be missed.
  const resetPasswordMutation = useMutation({
    mutationFn: (args: { id: string; email: string }) => resetStudentPassword(args.id),
    onSuccess: (res, args) => {
      setResetPasswordFor(null);
      setTempPassword({ email: args.email, password: res.tempPassword });
      invalidateStudentQueries();
    },
    onError: (e: Error) => toast.error(e.message || "Failed to reset password"),
  });

  const bulkDeleteStudents = useMutation({
    mutationFn: async (userIds: string[]) => {
      await Promise.all(
        userIds.map(async (userId) => {
          await adminManageUser("delete_user", { userId });
        }),
      );
    },
    onSuccess: () => {
      invalidateStudentQueries();
      toast.success(`${selectedStudentIds.size} student accounts deleted successfully`);
      setSelectedStudentIds(new Set());
    },
    onError: (e: Error) => toast.error("Bulk deletion failed: " + e.message),
  });

  // Membership-only bulk action, distinct from bulkDeleteStudents above: it unlinks the selection
  // from ONE group and leaves the accounts alone. Server-side and transactional, so a partial
  // removal can't leave students unlinked while the group's course access still stands.
  const bulkRemoveFromGroup = useMutation({
    mutationFn: (args: { groupId: string; studentIds: string[] }) =>
      removeTeamMembers(args.groupId, args.studentIds),
    onSuccess: (result) => {
      invalidateStudentQueries();
      const skipped = result.not_in_group.length;
      toast.success(
        `${result.removed} student${result.removed === 1 ? "" : "s"} removed from the group` +
          (skipped > 0 ? ` — ${skipped} were no longer members` : ""),
      );
      setSelectedStudentIds(new Set());
    },
    onError: (e: Error) => toast.error("Removal failed: " + e.message),
  });

  const callResetEndpoint = async (
    studentId: string,
    scope: "exam" | "study",
    courseId?: string,
  ) => {
    await resetStudentProgress(studentId, scope, courseId);
  };

  const resetExamSession = useMutation({
    mutationFn: (studentId: string) => callResetEndpoint(studentId, "exam"),
    onSuccess: () => {
      invalidateStudentQueries();
      toast.success("Exam reset successfully");
    },
    onError: (e: Error) => toast.error("Reset failed: " + e.message),
  });

  // "study" is a FULL reset server-side: module progress, exam attempts, assignment
  // submissions, course/checkpoint state, certificates (revoked) and training status.
  // Passing a courseId narrows all of that to that one course — sibling courses in the same
  // track and the student's training status are left alone.
  const resetStudyProgress = useMutation({
    mutationFn: (args: { studentId: string; courseId?: string }) =>
      callResetEndpoint(args.studentId, "study", args.courseId),
    onSuccess: (_data, args) => {
      invalidateStudentQueries();
      toast.success(args.courseId ? "Course progress reset" : "Student progress fully reset");
    },
    onError: (e: Error) => toast.error("Reset failed: " + e.message),
  });

  const forceCompleteTrainingMutation = useMutation({
    mutationFn: (args: { studentId: string; courseId?: string }) =>
      forceCompleteTraining(args.studentId, args.courseId),
    onSuccess: (result) => {
      invalidateStudentQueries();

      // Certificates are minted asynchronously (the PDF pipeline renders them a moment
      // later), so say how many were issued rather than leaving the admin to guess whether
      // the button did anything.
      const issued = result.certificatesIssued;
      toast.success(
        issued > 0
          ? `Training completed — ${issued} certificate${issued === 1 ? "" : "s"} issued (PDFs are generating)`
          : "Student training marked as completed",
      );

      // Anything that earned no certificate says why, instead of failing silently.
      result.notes.forEach((note) => toast.warning(note, { duration: 8000 }));
    },
    onError: (e: Error) => toast.error("Force completion failed: " + e.message),
  });

  // Name of the group currently filtered on, for the group-scoped bulk-removal copy.
  const filteredGroupName = teams.find((t: any) => t.id === projectFilter)?.name ?? "this group";

  const isAllSelected =
    visibleStudents.length > 0 && visibleStudents.every((p) => selectedStudentIds.has(p.id));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      const next = new Set(selectedStudentIds);
      visibleStudents.forEach((p) => next.delete(p.id));
      setSelectedStudentIds(next);
    } else {
      const next = new Set(selectedStudentIds);
      visibleStudents.forEach((p) => next.add(p.id));
      setSelectedStudentIds(next);
    }
  };

  const counts = useMemo(() => {
    if (!statusCounts) return {};
    return {
      "In Training": statusCounts.trainingStatus?.InTraining ?? 0,
      "Passed Training": statusCounts.trainingStatus?.Passed ?? 0,
      "Failed check": statusCounts.trainingStatus?.Failed ?? 0,
      Withdraw: statusCounts.trainingStatus?.WithdrawnDuringTraining ?? 0,
      "Withdraw after training": statusCounts.trainingStatus?.WithdrawnAfterTraining ?? 0,
      "Not recommended": statusCounts.trainingStatus?.NotRecommended ?? 0,
      "Not responding": statusCounts.notResponding ?? 0,
      "Black list": statusCounts.blacklisted ?? 0,
    };
  }, [statusCounts]);

  // Activity chip counts (fall back to 0 until the status-counts query resolves).
  const onlineCount = statusCounts?.online ?? 0;
  const offlineCount = statusCounts?.offline ?? 0;
  const neverCount = statusCounts?.neverActive ?? 0;

  // Average study progress across the currently-loaded rows (for the overview tile).
  const avgProgress = useMemo(() => {
    if (!studentProfiles.length) return 0;
    const sum = studentProfiles.reduce((acc, p) => acc + (p._progressPercent ?? 0), 0);
    return Math.round(sum / studentProfiles.length);
  }, [studentProfiles]);

  const getRole = (id: string) => rolesByUserId.get(id)?.[0] ?? "student";
  // Counted rather than just flagged so "More Filters" can show how many are hidden behind it.
  const advancedFilterCount = [
    selectedStatuses.size > 0,
    studyFilter !== "all",
    batchFilter !== "all",
    teamLeadFilter !== "all",
    groupAssignmentFilter !== "all",
    examFilter !== "all",
    progressFilter !== "all" || progressMin !== "" || progressMax !== "",
    attentionFilter !== "all",
    sortBy !== "createdat" || sortDir !== "desc",
  ].filter(Boolean).length;
  // Counts only what still lives behind the rail's "More filters" disclosure. The status chips
  // and the sort controls were lifted out of it, so advancedFilterCount would over-count here.
  const moreFiltersCount = [
    studyFilter !== "all",
    batchFilter !== "all",
    teamLeadFilter !== "all",
    groupAssignmentFilter !== "all",
    examFilter !== "all",
    progressFilter !== "all" || progressMin !== "" || progressMax !== "",
    attentionFilter !== "all",
  ].filter(Boolean).length;
  const hasActiveFilters =
    advancedFilterCount > 0 || projectFilter !== "all" || onlineFilter !== "all" || search !== "";

  const clearAllFilters = () => {
    setSearch("");
    setSelectedStatuses(new Set());
    setProjectFilter("all");
    setOnlineFilter("all");
    setStudyFilter("all");
    setTeamLeadFilter("all");
    setBatchFilter("all");
    setExamFilter("all");
    setProgressFilter("all");
    setProgressMin("");
    setProgressMax("");
    setGroupAssignmentFilter("all");
    setAttentionFilter("all");
    setSortBy("createdat");
    setSortDir("desc");
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* ── Page header ── */}
      <div className="flex items-center justify-between flex-wrap gap-4 pb-2 border-b border-muted">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10 text-primary shadow-inner">
              <GraduationCap className="h-7 w-7" />
            </div>
            Student Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage student accounts, track live dashboard activity, study progress, and configure
            exams.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
            className="gap-2 hover:bg-muted/50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            {isFetching ? "Loading…" : "Refresh"}
          </Button>
          {!isLeaderOnly && (
            <Button size="sm" onClick={() => setShowAddStudent(true)} className="gap-2">
              <UserPlus className="h-4 w-4" />
              Add Student
            </Button>
          )}
        </div>
      </div>

      {/* ── KPI strip + collapsible analytics ── */}
      <StudentAnalytics
        counts={counts}
        total={statusCounts?.total ?? 0}
        online={onlineCount}
        offline={offlineCount}
        neverActive={neverCount}
        avgProgress={avgProgress}
        loadedCount={studentProfiles.length}
        insightsOpen={insightsOpen}
        onToggleInsights={() => setInsightsOpen((v) => !v)}
      />

      {/* ── Filter rail + roster ── */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[288px_minmax(0,1fr)] xl:items-start">
        {/* ── Filter rail ──
            Search, the status/activity chips and the advanced fields used to be three
            full-width panels stacked above the table. Collected here they cost no vertical
            space at all, and the rail sticks while the roster scrolls. */}
        <aside className="space-y-4 rounded-2xl border bg-card p-4 shadow-sm xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Name, email, code, national ID, team lead…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 rounded-lg pl-9 text-[13px]"
            />
          </div>

          {/* Training status chips — one per row, so the long labels are never truncated. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Training Status
              </h3>
              {selectedStatuses.size > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedStatuses(new Set())}
                  className="h-5 px-2 text-[10px] text-muted-foreground hover:text-foreground"
                >
                  Clear
                </Button>
              )}
            </div>
            {/* Below xl the rail is a full-width band, where a vertical stack of chips would
                cost far more height than the panels this replaced. Chips flow there and only
                become full-width rows once the rail is a 288px column. */}
            <div className="flex flex-wrap gap-2 xl:flex-col">
              {TRAINING_STATUSES.map((s) => {
                const isSelected = selectedStatuses.has(s);
                return (
                  <button
                    key={s}
                    onClick={() => toggleStatusFilter(s)}
                    aria-pressed={isSelected}
                    className={[
                      "flex w-auto cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs font-medium shadow-sm transition-colors duration-200 xl:w-full",
                      isSelected
                        ? `${statusBadgeClass(s)} font-semibold opacity-100 ring-2 ring-primary ring-offset-2`
                        : "border-border bg-background/50 text-muted-foreground opacity-80 hover:bg-muted/50 hover:text-foreground",
                    ].join(" ")}
                  >
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${STATUS_DOT_COLORS[s as TrainingStatus]} ${isSelected ? "animate-pulse" : "opacity-65"}`}
                    />
                    <span className="flex-1 truncate text-left">{s}</span>
                    <Badge
                      variant="secondary"
                      className={`ml-auto h-4 shrink-0 border-transparent px-1.5 text-[10px] font-bold ${
                        isSelected
                          ? "bg-foreground/10 text-foreground"
                          : "bg-muted-foreground/10 text-muted-foreground"
                      }`}
                    >
                      {counts[s] ?? 0}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Live activity chips */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Live Activity
              </h3>
              {onlineFilter !== "all" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOnlineFilter("all")}
                  className="h-5 px-2 text-[10px] text-muted-foreground hover:text-foreground"
                >
                  Clear
                </Button>
              )}
            </div>
            {/* Below xl the rail is a full-width band, where a vertical stack of chips would
                cost far more height than the panels this replaced. Chips flow there and only
                become full-width rows once the rail is a 288px column. */}
            <div className="flex flex-wrap gap-2 xl:flex-col">
              {ACTIVITY_FILTERS.map((a) => {
                const isSelected = onlineFilter === a.key;
                const count =
                  a.key === "online"
                    ? onlineCount
                    : a.key === "offline"
                      ? offlineCount
                      : neverCount;
                return (
                  <button
                    key={a.key}
                    onClick={() => setOnlineFilter(isSelected ? "all" : a.key)}
                    aria-pressed={isSelected}
                    className={[
                      "flex w-auto cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs font-medium shadow-sm transition-colors duration-200 xl:w-full",
                      isSelected
                        ? `${a.selectedClass} font-semibold opacity-100 ring-2 ring-offset-2 ${a.ringClass}`
                        : "border-border bg-background/50 text-muted-foreground opacity-80 hover:bg-muted/50 hover:text-foreground",
                    ].join(" ")}
                  >
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${a.dotClass} ${isSelected ? "animate-pulse" : "opacity-65"}`}
                    />
                    <span className="flex-1 truncate text-left">{a.label}</span>
                    <Badge
                      variant="secondary"
                      className={`ml-auto h-4 shrink-0 border-transparent px-1.5 text-[10px] font-bold ${
                        isSelected ? a.badgeClass : "bg-muted-foreground/10 text-muted-foreground"
                      }`}
                    >
                      {count}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Group — a server-side facet, so it stays out of the "applied in the browser" set. */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-muted-foreground uppercase">Group</label>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="h-8 w-full rounded-lg text-xs" aria-label="Group">
                <SelectValue placeholder="All groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All groups</SelectItem>
                {teams.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="h-9 w-full cursor-pointer gap-2 rounded-lg px-3 text-xs font-semibold hover:bg-muted"
          >
            <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
            <span className="flex-1 text-left">
              {showAdvancedFilters ? "Hide filters" : "More filters"}
            </span>
            {moreFiltersCount > 0 && (
              <Badge
                variant="secondary"
                className="h-4 min-w-4 justify-center border-transparent bg-primary/15 px-1 text-[10px] font-bold text-primary"
              >
                {moreFiltersCount}
              </Badge>
            )}
            {showAdvancedFilters ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>

          {showAdvancedFilters && (
            <div className="grid grid-cols-1 gap-3 border-t border-muted/50 pt-3 sm:grid-cols-2 xl:grid-cols-1">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-muted-foreground uppercase">
                  Study
                </label>
                <Select value={studyFilter} onValueChange={setStudyFilter}>
                  <SelectTrigger className="w-full h-8 rounded-lg text-xs" aria-label="Study">
                    <SelectValue placeholder="All studies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All studies</SelectItem>
                    {uniqueStudies.map((s) => (
                      <SelectItem key={s} value={s} className="text-xs">
                        {studyLevelLabel(s)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-muted-foreground uppercase">
                  Team Lead
                </label>
                <Select value={teamLeadFilter} onValueChange={setTeamLeadFilter}>
                  <SelectTrigger className="w-full h-8 rounded-lg text-xs" aria-label="Team Lead">
                    <SelectValue placeholder="All team leads" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All team leads</SelectItem>
                    {teamLeaders.map((tl) => (
                      <SelectItem key={tl.id} value={tl.id} className="text-xs">
                        {tl.full_name || tl.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-muted-foreground uppercase">
                  Batch
                </label>
                <Select value={batchFilter} onValueChange={setBatchFilter}>
                  <SelectTrigger className="w-full h-8 rounded-lg text-xs" aria-label="Batch">
                    <SelectValue placeholder="All batches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All batches</SelectItem>
                    {batches.map((b) => (
                      <SelectItem key={b.id} value={b.id} className="text-xs">
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-muted-foreground uppercase">
                  Group Assignment
                </label>
                <Select value={groupAssignmentFilter} onValueChange={setGroupAssignmentFilter}>
                  <SelectTrigger
                    className="w-full h-8 rounded-lg text-xs"
                    aria-label="Group Assignment"
                  >
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="assigned" className="text-xs">
                      In a group
                    </SelectItem>
                    <SelectItem value="unassigned" className="text-xs">
                      No group
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-muted-foreground uppercase">
                  Exam Status
                </label>
                <Select value={examFilter} onValueChange={setExamFilter}>
                  <SelectTrigger className="w-full h-8 rounded-lg text-xs" aria-label="Exam Status">
                    <SelectValue placeholder="All exam states" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All exam states</SelectItem>
                    {EXAM_FILTER_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} className="text-xs">
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-muted-foreground uppercase">
                  Progress
                </label>
                <Select value={progressFilter} onValueChange={setProgressFilter}>
                  <SelectTrigger className="w-full h-8 rounded-lg text-xs" aria-label="Progress">
                    <SelectValue placeholder="Any progress" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any progress</SelectItem>
                    {PROGRESS_FILTER_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} className="text-xs">
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1.5 pt-0.5">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    inputMode="numeric"
                    placeholder="Min %"
                    value={progressMin}
                    onChange={(e) => setProgressMin(e.target.value)}
                    className="h-8 rounded-lg text-xs"
                    aria-label="Minimum progress percent"
                  />
                  <span className="text-xs text-muted-foreground">–</span>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    inputMode="numeric"
                    placeholder="Max %"
                    value={progressMax}
                    onChange={(e) => setProgressMax(e.target.value)}
                    className="h-8 rounded-lg text-xs"
                    aria-label="Maximum progress percent"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-muted-foreground uppercase">
                  Flags
                </label>
                <Select value={attentionFilter} onValueChange={setAttentionFilter}>
                  <SelectTrigger className="w-full h-8 rounded-lg text-xs" aria-label="Flags">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="flagged" className="text-xs">
                      Needs attention
                    </SelectItem>
                    <SelectItem value="clear" className="text-xs">
                      No flags
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Sort — lifted out of the advanced disclosure; it is used on its own often enough. */}
          <div className="border-t border-muted/50 pt-3">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-muted-foreground uppercase">
                Sort By
              </label>
              <div className="flex gap-2">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="h-8 flex-1 rounded-lg text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} className="text-xs">
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={sortDir} onValueChange={setSortDir}>
                  <SelectTrigger className="h-8 w-24 rounded-lg text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc" className="text-xs">
                      Desc
                    </SelectItem>
                    <SelectItem value="asc" className="text-xs">
                      Asc
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="h-8 w-full cursor-pointer justify-start gap-2 rounded-lg px-2 font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <FilterX className="h-4 w-4" />
              Clear all filters
            </Button>
          )}
        </aside>

        {/* ── Main table card ── */}
        <Card className="min-w-0">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  Students
                </CardTitle>
                <CardDescription>
                  {totalCount === 0
                    ? "No students match these filters"
                    : `Showing ${(pageNumber - 1) * pageSize + 1}–${
                        (pageNumber - 1) * pageSize + visibleStudents.length
                      } of ${totalCount}`}
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {!isLeaderOnly && selectedStudentIds.size > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground font-medium">
                      {selectedStudentIds.size} selected
                    </span>
                    {/* Group-scoped bulk removal. Offered ONLY while a single group is filtered,
                        because a student can belong to several groups — without a filter there is
                        no unambiguous "the group" to remove them from. */}
                    {projectFilter !== "all" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-2"
                            disabled={bulkRemoveFromGroup.isPending}
                          >
                            <X className="h-4 w-4" />
                            Remove from Group
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Remove {selectedStudentIds.size} student
                              {selectedStudentIds.size === 1 ? "" : "s"} from {filteredGroupName}?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              This only unlinks them from this group. Their accounts, progress and
                              certificates are kept, and they stay in any other groups they belong
                              to. The course access this group granted is revoked.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                bulkRemoveFromGroup.mutate({
                                  groupId: projectFilter,
                                  studentIds: Array.from(selectedStudentIds),
                                })
                              }
                            >
                              {bulkRemoveFromGroup.isPending ? "Removing..." : "Remove from Group"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="gap-2"
                          disabled={bulkDeleteStudents.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete Selected
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Delete {selectedStudentIds.size} student accounts?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Permanently delete the {selectedStudentIds.size} selected student
                            accounts. All progress, exam sessions, and certificates linked to these
                            accounts will be removed. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() =>
                              bulkDeleteStudents.mutate(Array.from(selectedStudentIds))
                            }
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {bulkDeleteStudents.isPending ? "Deleting..." : "Delete Accounts"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
                <div className="flex rounded-lg border p-0.5">
                  <Button
                    size="sm"
                    variant={viewMode === "card" ? "secondary" : "ghost"}
                    className="h-7 gap-1.5 px-2.5 text-xs"
                    onClick={() => setViewMode("card")}
                    title="Card view"
                    aria-pressed={viewMode === "card"}
                  >
                    <LayoutGrid className="h-3.5 w-3.5" /> Cards
                  </Button>
                  <Button
                    size="sm"
                    variant={viewMode === "list" ? "secondary" : "ghost"}
                    className="h-7 gap-1.5 px-2.5 text-xs"
                    onClick={() => setViewMode("list")}
                    title="List view"
                    aria-pressed={viewMode === "list"}
                  >
                    <Rows3 className="h-3.5 w-3.5" /> List
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Roster body — card grid or dense list, per the header toggle. */}
            {
              <div className="p-4">
                {isLoading ? (
                  viewMode === "list" ? (
                    <div className="divide-y divide-muted/50 overflow-hidden rounded-xl border">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-3 px-4 py-3">
                          <Skeleton className="h-4 w-4 rounded" />
                          <Skeleton className="h-9 w-9 rounded-full" />
                          <Skeleton className="h-4 w-40" />
                          <Skeleton className="ml-auto h-4 w-32" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <Card key={i} className="p-4 space-y-3">
                          <div className="flex items-center gap-3">
                            <Skeleton className="h-4 w-4 rounded" />
                            <Skeleton className="h-5 w-32" />
                          </div>
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-2/3" />
                        </Card>
                      ))}
                    </div>
                  )
                ) : visibleStudents.length === 0 ? (
                  <div className="py-16 text-center text-muted-foreground">
                    <div className="flex flex-col items-center gap-3">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                        <Users className="h-7 w-7 opacity-40" />
                      </div>
                      <div className="space-y-0.5">
                        <p className="font-medium text-foreground">No students found</p>
                        <p className="text-xs text-muted-foreground">
                          {hasActiveFilters
                            ? "Try adjusting or clearing your filters."
                            : "Students will appear here once added."}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : viewMode === "list" ? (
                  <div className="divide-y divide-muted/50 overflow-hidden rounded-xl border">
                    {/* One column header, instead of repeating the same micro-labels inside
                          every row. Hidden below lg, where the row wraps and no single header
                          line can stay aligned with the cells. */}
                    <div className="hidden items-center gap-x-4 bg-muted/35 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground lg:flex">
                      <span className={`min-w-[220px] flex-1 ${isLeaderOnly ? "" : "pl-[26px]"}`}>
                        Student
                      </span>
                      <span className="w-36 shrink-0">Study · Batch</span>
                      <span className="hidden w-40 shrink-0 xl:block">Project · Team Lead</span>
                      <span className="w-40 shrink-0">Progress</span>
                      <span className="w-36 shrink-0">Training Status</span>
                      <span className="w-32 shrink-0">Exam</span>
                      <span className="ml-auto w-[60px] shrink-0" />
                    </div>
                    {visibleStudents.map((p) => {
                      const { denominator, progressPct, completedCount } = getStudentProgress(p);
                      const role = getRole(p.id);
                      const onlineStatus = getOnlineStatus(p.last_seen_at);
                      const latest = latestExamByStudentId.get(p.id);

                      return (
                        <div
                          key={p.id}
                          onClick={() => setSelectedProfile(p)}
                          className="flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-3 bg-card px-4 py-3 transition-colors hover:bg-muted/40"
                        >
                          {/* Identity */}
                          <div className="flex min-w-[220px] flex-1 items-center gap-2.5">
                            {!isLeaderOnly && (
                              <div onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={selectedStudentIds.has(p.id)}
                                  onCheckedChange={() => toggleStudentSelection(p.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            )}
                            <StudentAvatar
                              name={p.full_name}
                              email={p.email}
                              avatarUrl={p.avatar_url}
                              seed={p.id}
                              className="h-9 w-9 shrink-0"
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="truncate text-sm font-semibold text-foreground">
                                  {p.full_name ?? (
                                    <span className="italic text-muted-foreground">No name</span>
                                  )}
                                </span>
                                {onlineStatus === "online" ? (
                                  <span
                                    className="relative flex h-2 w-2 shrink-0 animate-pulse"
                                    title="Online now"
                                  >
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                                  </span>
                                ) : onlineStatus === "offline" ? (
                                  <span
                                    className="inline-flex h-2 w-2 shrink-0 rounded-full bg-muted-foreground/40"
                                    title={`Offline · Last active ${p.last_seen_at ? new Date(p.last_seen_at).toLocaleString() : "unknown"}`}
                                  />
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="shrink-0 whitespace-nowrap border-warning/25 bg-warning-muted px-1 py-0 text-[9px] font-normal text-warning"
                                  >
                                    Never Opened
                                  </Badge>
                                )}
                                {role !== "student" && (
                                  <Badge
                                    variant="outline"
                                    className="shrink-0 whitespace-nowrap px-1 py-0 text-[9px] font-normal capitalize"
                                  >
                                    {role.replace("_", " ")}
                                  </Badge>
                                )}
                              </div>
                              <p className="truncate text-xs text-muted-foreground">
                                {p.email ?? "—"}
                                {p.student_id ? ` · ${p.student_id}` : ""}
                              </p>
                            </div>
                          </div>

                          {/* Study · Batch — labelled by the column header above. */}
                          <div className="hidden w-36 shrink-0 lg:block">
                            <p className="truncate text-xs font-medium text-foreground">
                              {[studyLevelLabel(p.study), p.batch].filter(Boolean).join(" · ") ||
                                "—"}
                            </p>
                          </div>

                          {/* Project · Team Lead */}
                          <div className="hidden w-40 shrink-0 xl:block">
                            {(() => {
                              const projectAndLead = [
                                p.project_assigned,
                                p.team_lead ? formatTeamLead(p.team_lead) : null,
                              ]
                                .filter(Boolean)
                                .join(" · ");
                              return (
                                <p
                                  className="truncate text-xs font-medium text-foreground"
                                  title={projectAndLead || undefined}
                                >
                                  {projectAndLead || "—"}
                                </p>
                              );
                            })()}
                          </div>

                          {/* Progress */}
                          <div
                            className="w-40 shrink-0 space-y-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-between gap-1 text-[11px]">
                              <span className="font-medium">
                                {completedCount}/{denominator} ({progressPct}%)
                              </span>
                              <div className="flex items-center">
                                {completedCount > 0 && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-5 w-5 text-warning hover:bg-warning-muted hover:text-warning"
                                    title="Reset All Progress"
                                    onClick={() => {
                                      if (
                                        confirm(
                                          `Reset ALL progress for ${p.full_name || p.email}?\n\nThis erases their module watch history, exam attempts and assignment submissions, sends every course back to 0%, revokes any certificate they hold, and puts them back to "In Training". Enrollments and group membership are kept. This cannot be undone.`,
                                        )
                                      ) {
                                        resetStudyProgress.mutate({ studentId: p.id });
                                      }
                                    }}
                                    disabled={resetStudyProgress.isPending}
                                  >
                                    <RotateCcw className="h-3 w-3" />
                                  </Button>
                                )}
                                {progressPct < 100 && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-5 w-5 text-success hover:bg-success-muted hover:text-success"
                                    title="Force Complete Study"
                                    onClick={() => {
                                      if (
                                        confirm(
                                          `Force complete study for ${p.full_name || p.email}?\n\nThis marks every published training module as completed and issues the certificates their courses earn — waiving any checkpoint or final exam they have not passed. Each certificate is recorded as an admin override.`,
                                        )
                                      ) {
                                        forceCompleteTrainingMutation.mutate({ studentId: p.id });
                                      }
                                    }}
                                    disabled={forceCompleteTrainingMutation.isPending}
                                  >
                                    <CheckCircle2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <Progress value={progressPct} className="h-1.5 w-full bg-muted" />
                          </div>

                          {/* Training status */}
                          <div className="w-36 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <StudentStatusEditor
                              lifecycle={p.training_lifecycle}
                              isBlacklisted={p.is_blacklisted}
                              isNotResponding={p.is_not_responding}
                              badgeClass={statusBadgeClass(p.training_status)}
                              triggerClass="h-7 text-[11px] border px-2 w-full"
                              onChange={(patch) =>
                                updateStudentStatus.mutate({ id: p.id, ...patch })
                              }
                            />
                          </div>

                          {/* Exam status */}
                          <div
                            className="flex w-32 shrink-0 items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {!latest ? (
                              <Badge
                                variant="outline"
                                className="w-full justify-center border-border bg-muted py-0.5 text-[10px] text-muted-foreground"
                              >
                                Not Taken
                              </Badge>
                            ) : (
                              <>
                                {latest.status === "completed" ? (
                                  latest.passed ? (
                                    <Badge className="w-full justify-center gap-1 border-success/25 bg-success-muted py-0.5 text-[10px] font-medium text-success hover:bg-success-muted">
                                      <CheckCircle2 className="h-3 w-3 text-success" />
                                      Passed
                                    </Badge>
                                  ) : (
                                    <Badge className="w-full justify-center gap-1 border-destructive/25 bg-destructive-muted py-0.5 text-[10px] font-medium text-destructive hover:bg-destructive-muted">
                                      <XCircle className="h-3 w-3 text-destructive" />
                                      Failed
                                    </Badge>
                                  )
                                ) : latest.status === "in_progress" ? (
                                  <Badge className="w-full justify-center gap-1 border-primary/20 bg-primary/10 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10">
                                    <span className="h-1 w-1 animate-pulse rounded-full bg-primary" />
                                    In Progress
                                  </Badge>
                                ) : (
                                  <Badge className="w-full justify-center gap-1 border-warning/25 bg-warning-muted py-0.5 text-[10px] font-medium text-warning hover:bg-warning-muted">
                                    Abandoned
                                  </Badge>
                                )}
                                {latest.biometric_image_url && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-6 w-6 shrink-0 text-primary hover:bg-primary/10 hover:text-primary"
                                    title="View Selfie"
                                    onClick={() =>
                                      window.open(latest.biometric_image_url, "_blank")
                                    }
                                  >
                                    <Camera className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6 shrink-0 text-warning hover:bg-warning-muted hover:text-warning"
                                  title="Reset Exam Sessions"
                                  onClick={() => {
                                    if (
                                      confirm(
                                        `Reset exam for ${p.full_name || p.email}? This will delete all their exam sessions and allow them to retake it.`,
                                      )
                                    ) {
                                      resetExamSession.mutate(p.id);
                                    }
                                  }}
                                  disabled={resetExamSession.isPending}
                                >
                                  <RotateCcw className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                          </div>

                          {/* Row actions */}
                          <div
                            className="ml-auto flex shrink-0 items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* SuperAdmin only, and never on your own row — see handleImpersonate. */}
                            {isSuperAdmin && p.id !== user?.id && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-amber-600 hover:bg-amber-500/10 hover:text-amber-600"
                                title={`View the platform as ${p.full_name || p.email}`}
                                onClick={() => handleImpersonate(p)}
                                disabled={impersonatingId === p.id}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {!isLeaderOnly && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    title="Delete account"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete student account?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Permanently delete <strong>{p.full_name || p.email}</strong>.
                                      This cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteStudent.mutate(p.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {visibleStudents.map((p) => {
                      const { denominator, progressPct, completedCount } = getStudentProgress(p);
                      const role = getRole(p.id);

                      return (
                        <div
                          key={p.id}
                          onClick={() => setSelectedProfile(p)}
                          className="cursor-pointer rounded-xl border bg-card p-4 hover:shadow-md transition-all space-y-4 relative group min-w-0 overflow-hidden"
                        >
                          {/* Header row */}
                          <div className="flex items-start justify-between gap-2 min-w-0">
                            <div className="flex items-start gap-2.5 min-w-0 flex-1">
                              {!isLeaderOnly && (
                                <div onClick={(e) => e.stopPropagation()} className="pt-1 shrink-0">
                                  <Checkbox
                                    checked={selectedStudentIds.has(p.id)}
                                    onCheckedChange={() => toggleStudentSelection(p.id)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              )}
                              <StudentAvatar
                                name={p.full_name}
                                email={p.email}
                                avatarUrl={p.avatar_url}
                                seed={p.id}
                                className="h-10 w-10 shrink-0"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span
                                    className="font-semibold text-foreground truncate max-w-full"
                                    title={p.full_name ?? undefined}
                                  >
                                    {p.full_name ?? (
                                      <span className="italic text-muted-foreground">No name</span>
                                    )}
                                  </span>
                                  {(() => {
                                    const status = getOnlineStatus(p.last_seen_at);
                                    if (status === "online") {
                                      return (
                                        <span
                                          className="flex h-2 w-2 relative animate-pulse shrink-0"
                                          title="Online now"
                                        >
                                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                                          <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
                                        </span>
                                      );
                                    } else if (status === "offline") {
                                      return (
                                        <span
                                          className="inline-flex rounded-full h-2 w-2 bg-muted-foreground/40 animate-none shrink-0"
                                          title={`Offline · Last active ${p.last_seen_at ? new Date(p.last_seen_at).toLocaleString() : "unknown"}`}
                                        />
                                      );
                                    } else {
                                      return (
                                        <Badge
                                          variant="outline"
                                          className="text-[9px] font-normal py-0 px-1 text-warning border-warning/25 bg-warning-muted whitespace-nowrap shrink-0"
                                        >
                                          Never Opened
                                        </Badge>
                                      );
                                    }
                                  })()}
                                </div>
                                {p.email && (
                                  <p className="truncate text-xs text-muted-foreground" title={p.email}>
                                    {p.email}
                                  </p>
                                )}
                                {p.student_id && (
                                  <p
                                    className="truncate font-mono text-[11px] text-muted-foreground mt-0.5"
                                    title={`ID: ${p.student_id}`}
                                  >
                                    ID: {p.student_id}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Detail Fields (2-col grid) */}
                          <div className="grid grid-cols-2 gap-2 text-xs border-t border-muted/50 pt-3">
                            <div className="min-w-0">
                              <p className="text-[10px] text-muted-foreground uppercase">Phone</p>
                              <p className="truncate font-medium text-foreground" title={p.phone ?? "—"}>
                                {p.phone ?? "—"}
                              </p>
                            </div>
                            <div className="min-w-0">
                              <p className="text-[10px] text-muted-foreground uppercase">
                                Study · Batch
                              </p>
                              <p
                                className="truncate font-medium text-foreground"
                                title={
                                  [studyLevelLabel(p.study), p.batch].filter(Boolean).join(" · ") ||
                                  "—"
                                }
                              >
                                {[studyLevelLabel(p.study), p.batch].filter(Boolean).join(" · ") ||
                                  "—"}
                              </p>
                            </div>
                            <div className="min-w-0">
                              <p className="text-[10px] text-muted-foreground uppercase">Project</p>
                              <div className="min-w-0 font-medium text-foreground mt-0.5">
                                {p.project_assigned ? (
                                  <Badge
                                    variant="secondary"
                                    className="truncate max-w-full inline-block text-[10px] px-1.5 py-0"
                                    title={p.project_assigned}
                                  >
                                    {p.project_assigned}
                                  </Badge>
                                ) : (
                                  "—"
                                )}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <p className="text-[10px] text-muted-foreground uppercase">
                                Team Lead
                              </p>
                              <p
                                className="truncate font-medium text-foreground"
                                title={formatTeamLead(p.team_lead)}
                              >
                                {formatTeamLead(p.team_lead)}
                              </p>
                            </div>
                          </div>

                          {/* Training Progress section */}
                          <div
                            className="space-y-1.5 pt-2 border-t border-muted/50 min-w-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">Progress</span>
                              <div className="flex items-center gap-1">
                                <span className="font-medium">
                                  {completedCount}/{denominator} ({progressPct}%)
                                </span>
                                {completedCount > 0 && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-5 w-5 text-warning hover:text-warning hover:bg-warning-muted shrink-0"
                                    title="Reset All Progress"
                                    onClick={() => {
                                      if (
                                        confirm(
                                          `Reset ALL progress for ${p.full_name || p.email}?\n\nThis erases their module watch history, exam attempts and assignment submissions, sends every course back to 0%, revokes any certificate they hold, and puts them back to "In Training". Enrollments and group membership are kept. This cannot be undone.`,
                                        )
                                      ) {
                                        resetStudyProgress.mutate({ studentId: p.id });
                                      }
                                    }}
                                    disabled={resetStudyProgress.isPending}
                                  >
                                    <RotateCcw className="h-3 w-3" />
                                  </Button>
                                )}
                                {progressPct < 100 && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-5 w-5 text-success hover:text-success hover:bg-success-muted shrink-0"
                                    title="Force Complete Study"
                                    onClick={() => {
                                      if (
                                        confirm(
                                          `Force complete study for ${p.full_name || p.email}?\n\nThis marks every published training module as completed and issues the certificates their courses earn — waiving any checkpoint or final exam they have not passed. Each certificate is recorded as an admin override.`,
                                        )
                                      ) {
                                        forceCompleteTrainingMutation.mutate({ studentId: p.id });
                                      }
                                    }}
                                    disabled={forceCompleteTrainingMutation.isPending}
                                  >
                                    <CheckCircle2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <Progress value={progressPct} className="h-1.5 w-full bg-muted" />
                          </div>

                          {/* Training & Exam dropdown/badges */}
                          <div
                            className="grid grid-cols-2 gap-3 pt-2 border-t border-muted/50 min-w-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="space-y-1 min-w-0">
                              <p className="text-[10px] text-muted-foreground uppercase">
                                Training Status
                              </p>
                              <StudentStatusEditor
                                lifecycle={p.training_lifecycle}
                                isBlacklisted={p.is_blacklisted}
                                isNotResponding={p.is_not_responding}
                                badgeClass={statusBadgeClass(p.training_status)}
                                triggerClass="h-7 text-[11px] border px-2 w-full"
                                onChange={(patch) =>
                                  updateStudentStatus.mutate({ id: p.id, ...patch })
                                }
                              />
                            </div>

                            <div className="space-y-1 min-w-0">
                              <p className="text-[10px] text-muted-foreground uppercase">
                                Exam Status
                              </p>
                              {(() => {
                                const latest = latestExamByStudentId.get(p.id);
                                if (!latest) {
                                  return (
                                    <Badge
                                      variant="outline"
                                      className="bg-muted text-muted-foreground border-border text-[10px] w-full justify-center py-0.5 truncate"
                                    >
                                      Not Taken
                                    </Badge>
                                  );
                                }
                                let badgeContent;
                                if (latest.status === "completed") {
                                  if (latest.passed) {
                                    badgeContent = (
                                      <Badge className="bg-success-muted text-success hover:bg-success-muted border-success/25 gap-1 w-full justify-center py-0.5 text-[10px] font-medium truncate">
                                        <CheckCircle2 className="h-3 w-3 text-success shrink-0" />
                                        <span className="truncate">Passed</span>
                                      </Badge>
                                    );
                                  } else {
                                    badgeContent = (
                                      <Badge className="bg-destructive-muted text-destructive hover:bg-destructive-muted border-destructive/25 gap-1 w-full justify-center py-0.5 text-[10px] font-medium truncate">
                                        <XCircle className="h-3 w-3 text-destructive shrink-0" />
                                        <span className="truncate">Failed</span>
                                      </Badge>
                                    );
                                  }
                                } else if (latest.status === "in_progress") {
                                  badgeContent = (
                                    <Badge className="bg-primary/10 text-primary hover:bg-primary/10 border-primary/20 gap-1 w-full justify-center py-0.5 text-[10px] font-medium truncate">
                                      <span className="h-1 w-1 rounded-full bg-primary animate-pulse shrink-0" />
                                      <span className="truncate">In Progress</span>
                                    </Badge>
                                  );
                                } else {
                                  badgeContent = (
                                    <Badge className="bg-warning-muted text-warning hover:bg-warning-muted border-warning/25 gap-1 w-full justify-center py-0.5 text-[10px] font-medium truncate">
                                      Abandoned
                                    </Badge>
                                  );
                                }

                                return (
                                  <div className="flex items-center gap-1 min-w-0">
                                    <div className="min-w-0 flex-1">
                                      {badgeContent}
                                    </div>
                                    {latest.biometric_image_url && (
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-6 w-6 text-primary hover:text-primary hover:bg-primary/10 shrink-0"
                                        title="View Selfie"
                                        onClick={() =>
                                          window.open(latest.biometric_image_url, "_blank")
                                        }
                                      >
                                        <Camera className="h-3.5 w-3.5" />
                                      </Button>
                                    )}
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-6 w-6 text-warning hover:text-warning hover:bg-warning-muted shrink-0"
                                      title="Reset Exam Sessions"
                                      onClick={() => {
                                        if (
                                          confirm(
                                            `Reset exam for ${p.full_name || p.email}? This will delete all their exam sessions and allow them to retake it.`,
                                          )
                                        ) {
                                          resetExamSession.mutate(p.id);
                                        }
                                      }}
                                      disabled={resetExamSession.isPending}
                                    >
                                      <RotateCcw className="h-3 w-3" />
                                    </Button>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Notes / Case History */}
                          {p.notes && (
                            <div className="text-[11px] bg-muted/40 p-2 rounded-lg text-muted-foreground border border-muted-foreground/10 min-w-0">
                              <p className="font-semibold text-[10px] uppercase text-muted-foreground/80 mb-0.5">
                                Notes
                              </p>
                              <p className="line-clamp-2 break-words">{p.notes}</p>
                            </div>
                          )}

                          {/* Bottom action row */}
                          <div
                            className="flex items-center justify-between gap-2 pt-2 border-t border-muted/50 min-w-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="text-[10px] text-muted-foreground capitalize truncate">
                              Role: {role.replace("_", " ")}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {/* SuperAdmin only, and never on your own row — see handleImpersonate. */}
                              {isSuperAdmin && p.id !== user?.id && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-amber-600 hover:text-amber-600 hover:bg-amber-500/10"
                                  title={`View the platform as ${p.full_name || p.email}`}
                                  onClick={() => handleImpersonate(p)}
                                  disabled={impersonatingId === p.id}
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {!isLeaderOnly && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                                      title="Delete account"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete student account?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Permanently delete <strong>{p.full_name || p.email}</strong>
                                        . This cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => deleteStudent.mutate(p.id)}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            }
            {totalCount > 0 && (
              <div className="border-t p-3">
                <PaginationControls
                  pageNumber={pageNumber}
                  pageSize={pageSize}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  hasNextPage={pageNumber < totalPages}
                  hasPreviousPage={pageNumber > 1}
                  onPageChange={setPageNumber}
                  onPageSizeChange={(size) => {
                    // Land on the page holding the first row currently on screen instead of
                    // snapping back to page 1.
                    const firstRow = (pageNumber - 1) * pageSize;
                    setPageSize(size);
                    setPageNumber(Math.floor(firstRow / size) + 1);
                  }}
                  isLoading={isLoading || isFetching}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {showAddStudent && <AddStudentDialog onClose={() => setShowAddStudent(false)} />}

      {/* ── Detail Panel Sheet ── */}
      <Sheet
        open={!!selectedProfile}
        onOpenChange={(open) => {
          if (!open) setSelectedProfile(null);
        }}
      >
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto bg-card border-l">
          <SheetHeader className="border-b pb-4 mb-4">
            <div className="flex items-center gap-3">
              {selectedProfile && (
                <button
                  type="button"
                  className="relative group/avatar shrink-0"
                  onClick={() => {
                    if (selectedProfile.avatar_url)
                      setAvatarLightboxUrl(selectedProfile.avatar_url);
                  }}
                  title={selectedProfile.avatar_url ? "Click to preview" : undefined}
                >
                  <StudentAvatar
                    name={selectedProfile.full_name}
                    email={selectedProfile.email}
                    avatarUrl={selectedProfile.avatar_url}
                    seed={selectedProfile.id}
                    className="h-12 w-12"
                  />
                  {selectedProfile.avatar_url && (
                    <span className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover/avatar:opacity-100 flex items-center justify-center transition-opacity">
                      <Maximize2 className="h-4 w-4 text-white" />
                    </span>
                  )}
                </button>
              )}
              <div className="min-w-0 flex-1">
                <SheetTitle className="flex items-center gap-2 text-base leading-tight">
                  <span className="truncate">
                    {selectedProfile?.full_name ?? "Student Details"}
                  </span>
                  {selectedProfile?.student_id && (
                    <Badge variant="outline" className="font-mono text-[10px] shrink-0">
                      {selectedProfile.student_id}
                    </Badge>
                  )}
                </SheetTitle>
                {selectedProfile?.email && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {selectedProfile.email}
                  </p>
                )}
              </div>
            </div>
          </SheetHeader>
          {selectedProfile && (
            <StudentDetailDialog
              profile={selectedProfile}
              role={getRole(selectedProfile.id)}
              onClose={() => setSelectedProfile(null)}
              onResetExam={() => resetExamSession.mutate(selectedProfile.id)}
              isResettingExam={resetExamSession.isPending}
              onResetStudy={(courseId) =>
                resetStudyProgress.mutate({ studentId: selectedProfile.id, courseId })
              }
              isResettingStudy={resetStudyProgress.isPending}
              onForceCompleteStudy={(courseId) =>
                forceCompleteTrainingMutation.mutate({ studentId: selectedProfile.id, courseId })
              }
              isCompletingStudy={forceCompleteTrainingMutation.isPending}
              onResetPassword={() =>
                setResetPasswordFor({ id: selectedProfile.id, email: selectedProfile.email ?? "" })
              }
              isResettingPassword={resetPasswordMutation.isPending}
              profiles={studentProfiles}
              roles={roles}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* ── Reset password confirm ── */}
      <AlertDialog
        open={!!resetPasswordFor}
        onOpenChange={(open) => {
          if (!open) setResetPasswordFor(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset password?</AlertDialogTitle>
            <AlertDialogDescription>
              A new temporary password will be generated for {resetPasswordFor?.email}. They will be
              required to change it on next login.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (resetPasswordFor) resetPasswordMutation.mutate(resetPasswordFor);
              }}
            >
              {resetPasswordMutation.isPending ? "Resetting…" : "Reset password"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Temporary password (shown once) ── */}
      <Dialog
        open={!!tempPassword}
        onOpenChange={(open) => {
          if (!open) setTempPassword(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temporary password</DialogTitle>
            <DialogDescription>
              Share this password with {tempPassword?.email}. It won&apos;t be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-3">
            <code className="flex-1 break-all text-sm">{tempPassword?.password}</code>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (tempPassword?.password) {
                  navigator.clipboard?.writeText(tempPassword.password);
                  toast.success("Copied to clipboard");
                }
              }}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPassword(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Avatar Lightbox ── */}
      <Dialog
        open={!!avatarLightboxUrl}
        onOpenChange={(open) => {
          if (!open) setAvatarLightboxUrl(null);
        }}
      >
        <DialogContent className="max-w-lg p-0 bg-transparent border-none shadow-none">
          <DialogHeader className="sr-only">
            <DialogTitle>Profile Picture Preview</DialogTitle>
            <DialogDescription>Full-size student profile picture.</DialogDescription>
          </DialogHeader>
          {avatarLightboxUrl && (
            <img
              src={avatarLightboxUrl}
              alt="Student profile picture"
              className="w-full h-auto rounded-lg object-contain max-h-[80vh]"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Add Student Dialog ───────────────────────────────────────────────────────
function AddStudentDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [formData, setFormData] = useState({
    full_name: "",
    student_id: "",
    studyLevel: 0,
    project_assigned: "",
    group_id: "",
    national_id: "",
    email: "",
    phone: "",
  });
  const [saving, setSaving] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const projectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (projectRef.current && !projectRef.current.contains(event.target as Node)) {
        setShowProjectDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: teams = [] } = useQuery({
    queryKey: ["admin-teams-list"],
    queryFn: fetchTeams,
  });

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
  const nationalIdTrimmed = formData.national_id.trim();
  const nationalIdValid = /^\d{14}$/.test(nationalIdTrimmed);
  const nationalIdTouched = nationalIdTrimmed.length > 0;

  function invalidateRoster() {
    qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.studentsGrid });
    qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.studentStatusCounts });
  }

  async function handleAdd() {
    if (!emailValid) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!nationalIdValid) {
      toast.error("National ID must be exactly 14 digits");
      return;
    }

    let groupId = formData.group_id;
    if (formData.project_assigned.trim() && !groupId) {
      const match = teams.find(
        (t) => t.name.toLowerCase() === formData.project_assigned.trim().toLowerCase(),
      );
      if (match) {
        groupId = match.id;
      } else {
        toast.error("Group not found — please pick a group from the suggestions list");
        return;
      }
    }

    setSaving(true);
    try {
      const created = await createStudent({
        email: formData.email.trim().toLowerCase(),
        nationalId: nationalIdTrimmed,
        studentCode: formData.student_id.trim() || null,
        studyLevel: formData.studyLevel,
        groupId: groupId || null,
      });

      const profileUpdates: Record<string, string> = {};
      if (formData.full_name.trim()) profileUpdates.fullName = formData.full_name.trim();
      if (formData.phone.trim()) profileUpdates.phone = formData.phone.trim();

      if (Object.keys(profileUpdates).length > 0) {
        try {
          await updateStudentProfile(created.id, profileUpdates);
        } catch (profileError) {
          const reason = profileError instanceof Error ? profileError.message : "";
          toast.warning(
            `Student created, but name/phone did not save${reason ? `: ${reason}` : ""} —` +
              " edit them from the roster.",
          );
          invalidateRoster();
          onClose();
          return;
        }
      }

      toast.success("Student added successfully!");
      invalidateRoster();
      onClose();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to add student");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Add Student
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2 text-xs">
          <div className="space-y-1 col-span-2">
            <label className="text-[10px] font-semibold text-muted-foreground uppercase">
              Full Name
            </label>
            <Input
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="e.g. Nada Ahmed Jalal"
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-semibold text-muted-foreground uppercase">
              Student Code
            </label>
            <Input
              value={formData.student_id}
              onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
              placeholder="e.g. S-18737"
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-semibold text-muted-foreground uppercase">
              Phone
            </label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="e.g. 1202527746"
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-semibold text-muted-foreground uppercase">
              Dashboard User Name (Email)
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="e.g. user@example.com"
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-semibold text-muted-foreground uppercase">
              Study
            </label>
            <Select
              value={String(formData.studyLevel)}
              onValueChange={(v) => setFormData({ ...formData, studyLevel: Number(v) })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select Study" />
              </SelectTrigger>
              <SelectContent>
                {STUDY_LEVELS.map((l) => (
                  <SelectItem key={l.value} value={String(l.value)} className="text-xs">
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 relative" ref={projectRef}>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase">
              Group Assigned
            </label>
            <div className="relative">
              <Input
                value={formData.project_assigned}
                onChange={(e) => {
                  setFormData({ ...formData, project_assigned: e.target.value, group_id: "" });
                  setProjectSearch(e.target.value);
                  setShowProjectDropdown(true);
                }}
                onFocus={() => setShowProjectDropdown(true)}
                placeholder="Type to search group..."
                className="h-8 text-xs pr-8"
              />
              <button
                type="button"
                onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                className="absolute right-2 top-2 h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
            {showProjectDropdown && (
              <div className="absolute z-50 left-0 right-0 mt-1 max-h-40 overflow-y-auto bg-popover text-popover-foreground border rounded-md shadow-md py-1">
                {teams
                  .filter((t) => t.name.toLowerCase().includes(projectSearch.toLowerCase()))
                  .map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground transition cursor-pointer"
                      onClick={() => {
                        setFormData({ ...formData, project_assigned: t.name, group_id: t.id });
                        setProjectSearch("");
                        setShowProjectDropdown(false);
                      }}
                    >
                      {t.name}
                    </button>
                  ))}
                {teams.filter((t) => t.name.toLowerCase().includes(projectSearch.toLowerCase()))
                  .length === 0 && (
                  <div className="px-3 py-1.5 text-xs text-muted-foreground italic text-center">
                    No groups found
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="space-y-1 col-span-2">
            <label className="text-[10px] font-semibold text-muted-foreground uppercase">
              National ID number
            </label>
            <Input
              value={formData.national_id}
              onChange={(e) => setFormData({ ...formData, national_id: e.target.value })}
              placeholder="National ID"
              className="h-8 text-xs"
            />
            <p
              className={[
                "text-[10px]",
                nationalIdTouched && !nationalIdValid
                  ? "text-destructive"
                  : "text-muted-foreground",
              ].join(" ")}
            >
              Exactly 14 digits — also the student's initial password ({nationalIdTrimmed.length}
              /14)
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t mt-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleAdd}
            disabled={saving || !emailValid || !nationalIdValid}
          >
            {saving ? "Adding..." : "Add Student"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Detail Dialog ────────────────────────────────────────────────────────────
function StudentDetailDialog({
  profile: p,
  role,
  onClose,
  onResetExam,
  isResettingExam,
  onResetStudy,
  isResettingStudy,
  onForceCompleteStudy,
  isCompletingStudy,
  onResetPassword,
  isResettingPassword,
  profiles,
  roles,
  isInline = false,
}: {
  profile: Profile;
  role: string;
  onClose: () => void;
  onResetExam?: () => void;
  isResettingExam?: boolean;
  /** `courseId` narrows the action to one course; undefined means the whole account. */
  onResetStudy?: (courseId?: string) => void;
  isResettingStudy?: boolean;
  onForceCompleteStudy?: (courseId?: string) => void;
  isCompletingStudy?: boolean;
  onResetPassword?: () => void;
  isResettingPassword?: boolean;
  profiles: Profile[];
  roles: UserRole[];
  isInline?: boolean;
}) {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState<string>(p.notes ?? "");

  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [fullName, setFullName] = useState(p.full_name ?? "");
  const [studentIdField, setStudentIdField] = useState(p.student_id ?? "");
  const [email, setEmail] = useState(p.email ?? "");
  const [phone, setPhone] = useState(p.phone ?? "");
  const [study, setStudy] = useState(p.study ?? "");
  // Batch is read-only (derived from group assignment) — removed from the edit form.
  const [projectAssigned, setProjectAssigned] = useState(p.project_assigned ?? "");
  const [teamLeadId, setTeamLeadId] = useState("__none__");
  const [nationalId, setNationalId] = useState(p.national_id ?? "");
  const [trainingStatus, setTrainingStatus] = useState(p.training_status ?? "In Training");
  const [accountStatus, setAccountStatus] = useState(p.status ?? "approved");
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const projectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (projectRef.current && !projectRef.current.contains(event.target as Node)) {
        setShowProjectDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: teams = [] } = useQuery({
    queryKey: ["admin-teams-list"],
    queryFn: fetchTeams,
  });

  // Team leaders come from the dedicated endpoint (users with the Teamleader role),
  // not from the student list — students are never team leaders.
  const { data: teamLeaders = [] } = useQuery<any[]>({
    queryKey: ["active-team-leaders"],
    queryFn: fetchActiveTeamLeaders,
  });

  // These three keys come from ADMIN_QUERY_KEYS so invalidateAllAdminState can reach them: the
  // drawer stays mounted through a reset / force-complete, so without that invalidation it keeps
  // showing the module progress, exam status and per-course completion from before the action.
  const { data: hasStudyProgress = false } = useQuery({
    queryKey: [...ADMIN_QUERY_KEYS.studentHasStudyProgress, p.id],
    // No dedicated study-progress probe endpoint; derive from exam activity instead.
    queryFn: async () => (await fetchStudentExamSessions(p.id)).length > 0,
  });

  const { data: studentExamSessions = [] } = useQuery({
    queryKey: [...ADMIN_QUERY_KEYS.studentExamSessions, p.id],
    queryFn: () => fetchStudentExamSessions(p.id),
  });

  // Every course this student can actually reach — standalone AND the ones inside their
  // tracks. The dashboard endpoint is the only source that covers track-derived access
  // (which has no Enrollment row of its own), and it carries the track name we group by.
  const { data: studentCourses = [] } = useQuery({
    queryKey: [...ADMIN_QUERY_KEYS.studentReachableCourses, p.id],
    queryFn: () => fetchStudentDashboardCourses(p.id),
  });

  // Which course the restudy/complete buttons act on. "__all__" keeps the historical
  // whole-account behaviour; anything else scopes the action to that single course.
  const [studyActionCourseId, setStudyActionCourseId] = useState("__all__");
  const scopedCourseId = studyActionCourseId === "__all__" ? undefined : studyActionCourseId;
  const scopedCourse = studentCourses.find((c) => c.course_id === studyActionCourseId);
  const scopedCourseLabel = scopedCourse?.course_title ?? "this course";

  // Resolve teamLeadId on load
  useEffect(() => {
    if (p.team_lead) {
      const match = teamLeaders.find(
        (tl) => (tl.full_name ?? "").toLowerCase() === (p.team_lead ?? "").toLowerCase(),
      );
      if (match) {
        setTeamLeadId(match.student_id || match.id);
      }
    }
  }, [p.team_lead, teamLeaders]);

  async function handleSave() {
    setSaving(true);
    try {
      // 1. Resolve team lead name from ID
      let resolvedTeamLead = p.team_lead;
      if (isEditingProfile) {
        if (teamLeadId === "__none__") {
          resolvedTeamLead = null;
        } else {
          const tlProfile = teamLeaders.find((tl) => (tl.student_id || tl.id) === teamLeadId);
          if (tlProfile) {
            resolvedTeamLead = tlProfile.full_name;
          }
        }
      }

      await updateStudentProfile(p.id, {
        fullName: isEditingProfile ? fullName.trim() || null : null,
        phone: isEditingProfile ? phone.trim() || null : null,
        // Batch is read-only; not sent from the edit form.
        studentId: isEditingProfile ? studentIdField.trim() || null : null,
        study: isEditingProfile ? study.trim() || null : null,
        projectAssigned: isEditingProfile ? projectAssigned.trim() || null : null,
        nationalId: isEditingProfile ? nationalId.trim() || null : null,
        teamLead: isEditingProfile ? resolvedTeamLead : undefined,
        trainingStatus: isEditingProfile ? trainingStatus : undefined,
        status: isEditingProfile ? accountStatus : (p.status ?? "approved"),
        email:
          isEditingProfile && email.trim().toLowerCase() !== (p.email ?? "").toLowerCase()
            ? email.trim().toLowerCase()
            : undefined,
        notes,
      });

      toast.success("Student updated successfully");
      qc.invalidateQueries({ queryKey: ["student-grid"] });
      qc.invalidateQueries({ queryKey: ["student-status-counts"] });
      qc.invalidateQueries({ queryKey: ["student-has-study-progress"] });
      qc.invalidateQueries({ queryKey: ["student-exam-sessions"] });
      onClose();
    } catch (e: any) {
      toast.error("Save failed: " + (e?.message ?? "unknown"));
    } finally {
      setSaving(false);
    }
  }

  const fields: [string, string | null | undefined][] = [
    ["Student Code", p.student_id],
    ["Full Name", p.full_name],
    ["Email", p.email],
    ["Phone", p.phone],
    ["Study", studyLevelLabel(p.study)],
    ["Batch", p.batch],
    ["Group Assigned", p.project_assigned],
    ["Team Lead", getTeamLeadDisplay(p.team_lead, profiles, roles)],
    ["National ID", p.national_id],
    ["Training Status", getDisplayTrainingStatus(p.training_status)],
    ["Account Role", role],
  ];

  // ── Computed study progress from grid DTO (module-level, same source as the card) ──
  // This ensures the sheet's progress matches the card's progress exactly.
  // `p` is Profile at the type level but always a StudentRow at runtime.
  const gridRow = p as any;
  const totalAssignedCourses = studentCourses.length;
  const completedCourses = studentCourses.filter((c) => c.is_completed).length;
  const overallPct =
    gridRow._progressPercent ??
    (totalAssignedCourses > 0
      ? Math.round(
          studentCourses.reduce((sum, c) => sum + (c.completion_pct ?? 0), 0) /
            totalAssignedCourses,
        )
      : 0);
  const moduleCompleted = gridRow._completedModules ?? 0;
  const moduleTotal = gridRow._totalModules ?? 0;

  const renderBody = () => (
    <div className="space-y-6 text-xs pt-1">
      {/* ── Profile Info / Editor ── */}
      {!isEditingProfile ? (
        <>
          {p.training_status && (
            <div className="flex items-center gap-2 -mt-1 mb-1">
              <span
                className={[
                  "inline-flex items-center text-xs px-2.5 py-0.5 rounded-full border font-medium",
                  statusBadgeClass(p.training_status),
                ].join(" ")}
              >
                {getDisplayTrainingStatus(p.training_status)}
              </span>
              <span className="text-xs text-muted-foreground capitalize">
                {role.replace("_", " ")}
              </span>
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm mt-1">
            {fields.map(([label, value]) => (
              <div key={label} className="col-span-1">
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">
                  {label}
                </dt>
                <dd className="font-medium break-all text-foreground">{value || "—"}</dd>
              </div>
            ))}
          </dl>
        </>
      ) : (
        <div className="grid grid-cols-2 gap-3 text-xs pt-1">
          {/* === PERSONAL INFO (Editable) === */}
          <div className="col-span-2">
            <p className="text-[10px] uppercase font-semibold text-primary mb-2 tracking-wider">
              Personal Information
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Full Name
            </label>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Phone
            </label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              National ID
            </label>
            <Input
              value={nationalId}
              onChange={(e) => setNationalId(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Email
            </label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Student Code
            </label>
            <Input
              value={studentIdField}
              onChange={(e) => setStudentIdField(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Study
            </label>
            <Select value={study || ""} onValueChange={setStudy}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select study level" />
              </SelectTrigger>
              <SelectContent>
                {STUDY_LEVELS.map((l) => (
                  <SelectItem key={l.name} value={l.name} className="text-xs">
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Profile Picture
            </label>
            <div className="flex items-center gap-2">
              <StudentAvatar
                name={p.full_name}
                email={p.email}
                avatarUrl={p.avatar_url}
                seed={p.id}
                className="h-10 w-10 shrink-0"
              />
              <div className="flex flex-col gap-1">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const formData = new FormData();
                        formData.append("image", file, file.name);
                        const headers = getAuthHeaders();
                        delete headers["Content-Type"];
                        const res = await fetch(`${API_BASE_URL}/Students/${p.id}/upload-avatar`, {
                          method: "POST",
                          headers,
                          body: formData,
                        });
                        if (res.ok) {
                          toast.success("Avatar uploaded");
                          qc.invalidateQueries({ queryKey: ["student-grid"] });
                        } else {
                          toast.error("Upload failed");
                        }
                      } catch {
                        toast.error("Upload failed");
                      }
                    }}
                  />
                  <span className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline cursor-pointer">
                    <ImagePlus className="h-3 w-3" /> Upload
                  </span>
                </label>
                {p.avatar_url && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-[10px] text-destructive hover:underline"
                    onClick={async () => {
                      if (!confirm("Remove this student's profile picture?")) return;
                      try {
                        const res = await fetch(`${API_BASE_URL}/Students/${p.id}/delete-avatar`, {
                          method: "DELETE",
                          headers: getAuthHeaders(),
                        });
                        if (res.ok) {
                          toast.success("Avatar removed");
                          qc.invalidateQueries({ queryKey: ["student-grid"] });
                        } else {
                          toast.error("Remove failed");
                        }
                      } catch {
                        toast.error("Remove failed");
                      }
                    }}
                  >
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* === ACADEMIC / TRAINING INFO (Read-Only) === */}
          <div className="col-span-2 mt-3">
            <p className="text-[10px] uppercase font-semibold text-muted-foreground/70 mb-2 tracking-wider">
              Academic / Training (Read-Only)
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Batch
            </label>
            <Input
              value={p.batch ?? ""}
              disabled
              className="h-8 text-xs bg-muted/50 cursor-not-allowed"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Group Assigned
            </label>
            <Input
              value={projectAssigned}
              disabled
              className="h-8 text-xs bg-muted/50 cursor-not-allowed"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Team Lead
            </label>
            <Input
              value={getTeamLeadDisplay(p.team_lead, profiles, roles) ?? ""}
              disabled
              className="h-8 text-xs bg-muted/50 cursor-not-allowed"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Training Status
            </label>
            <Input
              value={getDisplayTrainingStatus(p.training_status) ?? ""}
              disabled
              className="h-8 text-xs bg-muted/50 cursor-not-allowed"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase font-semibold text-muted-foreground">
              Account Status
            </label>
            <Select value={accountStatus} onValueChange={setAccountStatus}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved" className="text-xs">
                  Active (Approved)
                </SelectItem>
                <SelectItem value="suspended" className="text-xs">
                  Suspended
                </SelectItem>
                <SelectItem value="pending" className="text-xs">
                  Pending
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* ── Study Progress ── */}
      <div className="space-y-4 border-t pt-4">
        <h4 className="font-bold text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <BookOpen className="h-3.5 w-3.5 text-primary" />
          Study Progress
        </h4>

        {/* Overall summary + per-course breakdown */}
        {totalAssignedCourses === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-xs">
            No courses assigned to this student yet.
          </div>
        ) : (
          <div className="space-y-3">
            {/* Summary line + overall bar — uses grid DTO module counts for consistency with card */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {moduleCompleted} of {moduleTotal} module{moduleTotal !== 1 ? "s" : ""} completed
                  {totalAssignedCourses > 0 && (
                    <span className="text-muted-foreground/70 ml-1">
                      ({completedCourses}/{totalAssignedCourses} course
                      {totalAssignedCourses !== 1 ? "s" : ""})
                    </span>
                  )}
                </span>
                <span className="font-semibold text-foreground">{overallPct}%</span>
              </div>
              <Progress value={overallPct} className="h-2 w-full bg-muted" />
            </div>

            {/* Per-course rows */}
            <div className="divide-y border rounded-lg bg-background/50">
              {studentCourses.map((c) => (
                <div key={c.course_id} className="flex items-center gap-3 p-2.5 text-xs">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground truncate">{c.course_title}</p>
                    {c.track_name && (
                      <p className="text-[10px] text-muted-foreground truncate">{c.track_name}</p>
                    )}
                  </div>
                  <div className="w-20 shrink-0">
                    <Progress value={c.completion_pct ?? 0} className="h-1.5 bg-muted" />
                  </div>
                  <span className="w-10 text-right text-[11px] font-medium text-muted-foreground tabular-nums">
                    {c.completion_pct ?? 0}%
                  </span>
                  {c.is_completed ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                  ) : (
                    <span className="h-3.5 w-3.5 shrink-0" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admin actions — scope selector + reset / complete buttons */}
        <div className="space-y-2 pt-2 border-t border-muted/50 mt-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Apply to</label>
            <Select value={studyActionCourseId} onValueChange={setStudyActionCourseId}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="All courses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All courses (entire account)</SelectItem>
                {studentCourses.map((c) => (
                  <SelectItem key={c.course_id} value={c.course_id}>
                    {c.track_name ? `${c.track_name} — ${c.course_title}` : c.course_title}
                    {typeof c.completion_pct === "number" ? ` (${c.completion_pct}%)` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {scopedCourseId && (
              <p className="text-[11px] leading-snug text-muted-foreground">
                Only this course is affected. Other courses in the same track keep their progress
                and certificates, and the student's training status is unchanged.
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-warning hover:bg-warning-muted hover:text-warning gap-1.5 h-8 border-warning/30"
              onClick={() => {
                const message = scopedCourseId
                  ? `Reset progress for "${scopedCourseLabel}" only?\n\nThis erases ${p.full_name || p.email}'s module completions, exam attempts and assignment submissions for that one course, sends it back to 0% and revokes its certificate. Every other course, the track certificate and their training status are left alone. This cannot be undone.`
                  : `Reset ALL progress for ${p.full_name || p.email}?\n\nThis erases their module completions, exam attempts and assignment submissions, sends every course back to 0%, revokes any certificate they hold, and puts them back to "In Training". Enrollments and group membership are kept. This cannot be undone.`;
                if (confirm(message)) {
                  onResetStudy?.(scopedCourseId);
                }
              }}
              disabled={isResettingStudy}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {scopedCourseId ? "Reset This Course" : "Reset All Progress"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-success hover:bg-success-muted hover:text-success gap-1.5 h-8 border-success/25"
              onClick={() => {
                const message = scopedCourseId
                  ? `Mark "${scopedCourseLabel}" complete for ${p.full_name || p.email}?\n\nThis completes that course's published modules and issues the certificate it earns — waiving any checkpoint or final exam they have not passed. No other course is touched, and their training status is not promoted.`
                  : `Force complete study for ${p.full_name || p.email}?\n\nThis marks every published training module as completed and issues the certificates their courses earn — waiving any checkpoint or final exam they have not passed. Each certificate is recorded as an admin override.`;
                if (confirm(message)) {
                  onForceCompleteStudy?.(scopedCourseId);
                }
              }}
              disabled={isCompletingStudy}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {scopedCourseId ? "Complete This Course" : "Complete Study"}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Exam Sessions ── */}
      <div className="space-y-3 border-t pt-4">
        <h4 className="font-bold text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <GraduationCap className="h-3.5 w-3.5 text-primary" />
          Exam Sessions
        </h4>

        {studentExamSessions.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-xs">
            No exam sessions found for this student.
          </div>
        ) : (
          <div className="divide-y border rounded-lg bg-background/50">
            {studentExamSessions.map((session: any, idx: number) => {
              const statusKey = String(session.status).toLowerCase();
              let badgeCls = "bg-muted text-muted-foreground border-border";
              let label = session.status;
              if (statusKey === "completed" && session.passed) {
                badgeCls = "bg-success-muted text-success border-success/25";
                label = "Passed";
              } else if (statusKey === "completed" && !session.passed) {
                badgeCls = "bg-destructive-muted text-destructive border-destructive/25";
                label = "Failed";
              } else if (statusKey === "in_progress") {
                badgeCls = "bg-primary/10 text-primary border-primary/20";
                label = "In Progress";
              } else if (statusKey === "terminated") {
                badgeCls = "bg-destructive/10 text-destructive border-destructive/20";
                label = "Terminated";
              } else if (statusKey === "abandoned") {
                badgeCls = "bg-warning-muted text-warning border-warning/25";
                label = "Abandoned";
              } else if (statusKey === "pending_grading" || statusKey === "pendinggrading") {
                badgeCls = "bg-warning/10 text-warning border-warning/20";
                label = "Pending Grading";
              }

              return (
                <div key={session.id ?? idx} className="flex items-center gap-3 p-2.5 text-xs">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground truncate">
                      {session.exam_title ?? "Exam"}
                    </p>
                    {session.started_at && (
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(session.started_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-[10px] py-0.5 px-2 font-medium shrink-0 ${badgeCls}`}
                  >
                    {label}
                  </Badge>
                  {session.score != null && (
                    <span className="text-[11px] font-semibold tabular-nums shrink-0">
                      {session.score}%
                    </span>
                  )}
                  {session.biometric_image_url && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-primary hover:text-primary hover:bg-primary/10 shrink-0"
                      title="View Selfie"
                      onClick={() => window.open(session.biometric_image_url, "_blank")}
                    >
                      <Camera className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {studentExamSessions.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-warning hover:bg-warning-muted hover:text-warning gap-1.5 h-8 border-warning/30"
            onClick={() => {
              if (
                confirm(
                  `Reset exam for ${p.full_name || p.email}? This will delete all their exam sessions and allow them to retake it.`,
                )
              ) {
                onResetExam?.();
              }
            }}
            disabled={isResettingExam}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset Exam
          </Button>
        )}

        <StudentActivityLog studentId={p.id} />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header controls (Edit Profile + Reset Password) */}
      <div className="flex gap-2 justify-end pb-3 border-b border-muted/50 -mt-2">
        {!isEditingProfile ? (
          <>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => setIsEditingProfile(true)}
            >
              <Edit className="h-3.5 w-3.5" />
              Edit Profile
            </Button>
            {onResetPassword && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={onResetPassword}
                disabled={isResettingPassword}
              >
                <KeyRound className="h-3.5 w-3.5" />
                {isResettingPassword ? "Resetting…" : "Reset Password"}
              </Button>
            )}
          </>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs border-primary text-primary"
            onClick={() => setIsEditingProfile(false)}
          >
            Cancel Edit
          </Button>
        )}
      </div>

      <div>{renderBody()}</div>
    </div>
  );
}
